/**
 * EHR write-back — pure, testable logic + the MAIN-world stage injector.
 *
 * Two transports behind one seam (ACT-1242):
 *  - `commit` — same-origin REST POST from the content-script isolated world
 *    (fetch + CSRF from document.cookie). Persists autonomously; needs an
 *    in-app confirm. Implemented by `performWriteBack` here.
 *  - `stage`  — app-object write into the clinician's open editor via
 *    `window.el8.appchart.mgr.notes.addText`. `window.el8` only exists in the
 *    page MAIN world, so the sidepanel injects `stageInPage` with
 *    `chrome.scripting.executeScript({ world: "MAIN", func: stageInPage })`.
 *    The clinician's own Save/Sign in Elation is the gate.
 *
 * The pure pieces (`resolveSectionRef`, `guardStageText`, `mapEngineToResult`,
 * commit logic) are unit-tested under jsdom with mocks. The live `window.el8`
 * calls live only inside `stageInPage`, which is serialized by executeScript and
 * therefore MUST be fully self-contained (no external closure references) —
 * verified by typecheck + build, exercised live in the sandbox.
 */

/** Commit policy selected by the consuming feature. Default resolved by caller = "stage". */
export type WriteBackMode = "commit" | "stage";

export type WriteBackErrorCode =
  // commit (REST)
  | "HTTP_4XX"
  | "HTTP_5XX"
  | "CSRF_MISSING"
  | "READ_AFTER_WRITE_FAILED"
  // stage (app-object, MAIN world)
  | "EL8_UNAVAILABLE"
  | "OPEN_NOTE_MISMATCH"
  | "ADDBULLET_THREW"
  | "INJECTION_BLOCKED"
  | "STAGE_NOT_IMPLEMENTED"
  | "ENGINE_UNSUPPORTED"
  | "SECTION_UNRESOLVED"
  | "SECTION_NOT_WRITABLE"
  // A per-system field was written and the value could NOT be read back out of
  // the live document afterwards. See the read-back invariant in `stageInPage`.
  | "WRITE_UNVERIFIED"
  // shared guards
  | "NO_OPEN_NOTE"
  | "SIGNED_NOTE_UNSUPPORTED"
  | "EMPTY_TEXT"
  // patient-match guards
  | "PATIENT_TAB_NOT_FOUND"
  | "PATIENT_MISMATCH";

export interface WriteBackRequest {
  patientId: string;
  noteId: string;
  /** commit: Elation note section (spike-confirmed list). */
  category: string;
  text: string;
  mode: WriteBackMode;
  /**
   * stage: ordered intent — preferred Elation reference names. `stageInPage`
   * picks the first that maps to a real section for the live template
   * (`resolveSectionRef`). Ignored by commit. Ignored when `sectionKey` is set.
   */
  preferredRefs?: string[];
  /**
   * PER-SECTION EXACT-KEY WRITE (ACT-1242 fix). An exact `_inputSections` key
   * (lowercase, e.g. "assessplan"/"narrative") read from the live open note.
   * When set, STAGE bypasses `mapReferenceFn` entirely and writes this exact
   * section — no fuzzy mapping to get wrong (the section-mismapping bug). COMMIT
   * passes the Capitalized key as `category` (best-effort; different vocabulary).
   */
  sectionKey?: string;
  /**
   * PROVENANCE CHANNEL (per-system write). The drafted body's lines as
   * `encodeSectionLines` JSON (`@astrana/ui/scribe`) — each line's verbatim
   * `text` alongside the
   * `systemLabel`/`finding` the DRAFTER assigned it.
   *
   * A string, not an array, because the only consumer is `stageInPage`, which
   * `chrome.scripting.executeScript` serializes: the arg tuple is strings-only
   * by construction and every hop in between (bridge postMessage → sidepanel →
   * executeScript) can carry a string without knowing the shape. `""`/absent
   * means "no provenance", and the writer falls back to parsing `text`.
   *
   * Why it exists: without it the injected writer had to re-derive which system
   * a line named by re-reading the text it was about to write, and every one of
   * those reads mutated the physician's words — a `Chest:` prefix deleted and
   * the finding filed under Respiratory, a leading `>` stripped off
   * `>2 pillow orthopnea`. Provenance travels beside the text so the text is
   * never read for anything but writing it out verbatim.
   */
  linesJson?: string;
  sequence?: number;
  parentId?: string | null;
  noteState?: "open" | "signed";
}

export interface WriteBackResult {
  ok: boolean;
  /** HTTP status for commit; synthetic (200/0) for stage. */
  status: number;
  /** commit: re-GET confirmed the text is present. Always false for stage. */
  readAfterWrite: boolean;
  /** Which transport actually ran. */
  mode?: WriteBackMode;
  /** stage: the note engine detected in the page ("legacy" | "vn2" | "nonvisit"). */
  engine?: string;
  /** stage: the reference name resolved for the live template. */
  section?: string;
  errorCode?: WriteBackErrorCode;
  detail?: string;
  /**
   * stage/per-system: the write succeeded but the per-system fix did not fully
   * apply — see {@link StageInPageResult.degraded}. Carried across the message
   * boundary so the panel can say so instead of reporting a plain success.
   */
  degraded?: boolean;
}

export interface WriteBackDeps {
  fetchFn: typeof fetch;
  cookie: string;
}

/**
 * Spike-confirmed (2026-07-01) categories a full note accepted for the COMMIT
 * (REST) transport. Invalid categories return HTTP 500 (not a helpful 4xx), so
 * the picker offers only known-good ones. Template-specific derivation is a
 * follow-up once the note-model section list is captured.
 */
// TODO(ACT-1242): derive valid sections from the note-model instead of this sandbox snapshot
export const DEFAULT_WRITE_CATEGORIES = [
  "Reason",
  "Hpi",
  "Problem",
  "Allergies",
  "Ros",
  "Assessment",
  "Plan",
  "Followup",
  "Narrative",
] as const;

/**
 * Reference names that map to a valid section on BOTH observed templates
 * (Simple + SOAP). Safe to stage without template knowledge. (Findings §D2.)
 */
export const CROSS_TEMPLATE_SAFE_REFS = [
  "reason",
  "followup",
  "instr",
  "referenced",
  "orders",
] as const;

/**
 * Curated set of reference-name intents offered by the panel's section picker
 * for STAGE. `resolveSectionRef` + the in-page guard decide validity at runtime
 * against the live template — never hardcode a single name across templates.
 * Body text: `narrative` (Simple) vs `assessment`/`plan` (SOAP).
 */
export const CURATED_SECTION_REFS = [
  "assessment",
  "plan",
  "narrative",
  "hpi",
  "ros",
  "pe",
  "reason",
  "followup",
  "instr",
] as const;

export function readCsrfToken(cookie: string): string | null {
  return cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/)?.[1] ?? null;
}

export function buildBulletFormBody(req: WriteBackRequest): string {
  const data = {
    id: null,
    note_id: Number(req.noteId),
    parent_id: req.parentId ?? null,
    sequence: req.sequence ?? 999,
    text: req.text,
    category: req.category,
    is_append: false,
    ai_generate: false,
  };
  return "data=" + encodeURIComponent(JSON.stringify(data));
}

// ── Pure stage helpers (unit-tested; also inlined into stageInPage) ──────────

/**
 * Given the live template's `mapReferenceFn` and its `_inputSections` map, pick
 * the first preferred reference name that maps to a real section. Returns null
 * when none resolve (caller must refuse — staging an invalid section corrupts
 * the note; see findings "Corruption root cause").
 *
 * NOTE: the logic here is duplicated verbatim inside `stageInPage` because that
 * function is serialized by executeScript and cannot close over this. Keep them
 * in sync — this copy is the one under test.
 */
export function resolveSectionRef(
  mapFn: (ref: string) => string,
  inputSections: Record<string, unknown>,
  preferredRefs: string[],
): string | null {
  for (const ref of preferredRefs) {
    const key = mapFn(ref);
    if (key && Object.prototype.hasOwnProperty.call(inputSections, key) && inputSections[key]) {
      return ref;
    }
  }
  return null;
}

/**
 * Empty-text guard. `addText("")` does not throw but appends a junk blank line
 * (findings §D4), so refuse empty/whitespace up front.
 */
export function guardStageText(text: string): "EMPTY_TEXT" | null {
  return !text || !text.trim() ? "EMPTY_TEXT" : null;
}

export type StageEngine = "legacy" | "nonvisit" | "vn2" | "none";

/**
 * Map a detected engine to a blocking error code, or null when supported.
 * THIS BUILD supports the legacy engine only. VN2 + nonvisit are typed
 * ENGINE_UNSUPPORTED — VN2 is being live-verified separately and slots in at
 * the extension point in `stageInPage`.
 */
export function mapEngineToResult(engine: StageEngine): WriteBackErrorCode | null {
  switch (engine) {
    case "legacy":
      return null;
    case "vn2":
    case "nonvisit":
      return "ENGINE_UNSUPPORTED";
    case "none":
    default:
      return "NO_OPEN_NOTE";
  }
}

// ── Per-section model (ACT-1242) ─────────────────────────────────────────────

/** One actual section of the currently-open note (read live from `window.el8`). */
export interface EhrNoteSection {
  /** Exact `_inputSections` key (lowercase, e.g. "assessplan"). The write target. */
  key: string;
  /** Pretty label from `sec.getName()` (or the key as a fallback). */
  label: string;
  /** The section's current content, for muted read-only context in the panel. */
  currentText: string;
  /**
   * Whether this section accepts a free-text bullet write. STRUCTURED sections
   * (orders, meds, problems, allergies, vitals, dx/cpt codes) do NOT — writing
   * a free-text bullet into them via `addBullet`+`addEntry` fails or corrupts
   * the note (ACT-1242 bug 2). Non-writable sections are hidden from the write
   * UI and refused defensively in `stageInPage`.
   *
   * NOTE: a `multilist` section is writable ONLY when its key is in
   * {@link MULTILIST_WRITABLE_KEYS} (today: `instr`). The write mechanism was
   * verified live on 2026-08-05, contradicting the original assumption that it
   * corrupts — but mechanical safety is not clinical suitability, so the grant
   * is per key, not per widget type.
   */
  writable: boolean;
}

/**
 * Exact `_inputSections` keys (normalized: lowercase, alphanumerics only) that
 * are STRUCTURED lists/codes, not free text. A free-text bullet write into one
 * of these either throws or leaves the note in a corrupt state, so they are the
 * authoritative blocklist for `isSectionKeyWritable` and the defensive refusal
 * in `stageInPage`. Kept deliberately broad across template naming variants.
 */
export const STRUCTURED_SECTION_KEYS: ReadonlySet<string> = new Set([
  "order", "orders",
  // `referenced` is a `textlist` of DOCUMENT REFERENCES on every template seen.
  // Key-blocked as a backstop because it was previously protected by the live
  // type read ALONE, and that read fails open when `getType` is absent or throws.
  "referenced",
  // NOT `problem` (singular). On the legacy SOAP template that key is the
  // free-text subjective/HPI section — live-verified `getType() === "textarea"`,
  // rendered under the label "Prob:". Blocking it hid the single most likely
  // destination for generated prose, and the note's own HPI had nowhere to go.
  // Three independent signals agreed: the live type read; the ACT-1242 stage
  // contract, whose SOAP mapping lists `hpi -> problem` as valid; and the
  // shipped extension scraper, which reads this section AS the note's
  // subjective narrative (`subjective: k.prob ?? k.s ?? k.hpi`). The plural
  // forms stay blocked — those ARE the structured problem list.
  "problems", "problemlist",
  "med", "meds", "medication", "medications", "medlist", "medicationlist",
  "allergy", "allergies", "allergylist",
  "vital", "vitals",
  "data",
  "cpt", "cptcode", "cptcodes",
  "dx", "dxcode", "dxcodes", "diagnosis", "diagnoses",
  "prescription", "prescriptions",
  "referral", "referrals",
  "lab", "labs",
  "imaging",
  "immunization", "immunizations", "vaccine", "vaccines",
]);

/**
 * Sections that are technically writable but clinically WRONG destinations for
 * generated prose. Deliberately separate from {@link STRUCTURED_SECTION_KEYS}:
 * those keys would CORRUPT the note, these would merely put an assessment in a
 * procedures list. Conflating them makes the refusal message lie to whoever
 * debugs the next failed write.
 *
 * A PATTERN, not an enumeration, because enumerating requires guessing
 * spellings: `procedure` was observed live, but `procedures2`, `procedurenote`,
 * `officeprocedure` and `dateofprocedure` are all plausible template names and
 * a list would miss them. `getType()` cannot help here — `procedure`, `pe` and
 * `ros` are all `listeditor`; the widget type says nothing about clinical
 * meaning, so the name is the only available signal.
 *
 * Scoped tightly to `procedure` ON PURPOSE. `surgical`/`surgery` were
 * considered and left OUT: surgical history is plausibly free-text narrative,
 * and over-blocking silently removes a destination a clinician needs, with no
 * message they can act on — the same defect as under-blocking, inverted.
 */
export const POLICY_EXCLUDED_SECTION_PATTERN = /procedure/;

/**
 * Live `getType()` values that can NEVER take a free-text bullet, whatever the
 * key. `textlist` (`referenced`, `orders`) holds DOCUMENT REFERENCES — their
 * `getJsonData()` returns nothing at all — so prose has no meaning there.
 *
 * `multilist` is deliberately NOT here, and equally deliberately not simply
 * allowed: see {@link MULTILIST_WRITABLE_KEYS}.
 *
 * Mirrored verbatim inside `readNoteSectionsInPage` and `stageInPage`, which
 * `executeScript` serializes so they cannot reference this constant. The parity
 * test compares all three copies as sets — keep the inline ones written in the
 * same array-literal shape or that extraction stops working.
 */
export const STRUCTURED_SECTION_TYPES: ReadonlySet<string> = new Set(["textlist"]);

/**
 * The ONLY keys for which a `multilist` section may be written.
 *
 * A `multilist` write was proven safe live (2026-08-05) on exactly one section
 * — `instr` ("Care Plan:") — on two templates. Granting the whole `multilist`
 * WIDGET CLASS on that evidence over-generalizes: any non-blocklisted key that
 * happens to be a multilist (a customer-named goals/screening/HCC list, or a
 * template whose singular `problem` is the STRUCTURED problem list rather than
 * the SOAP "Prob:" textarea) would silently become a prose destination, with
 * the hand-maintained key blocklist as the only remaining defence — and that
 * list has already been patched once for a miss (`procedure`).
 *
 * Note the specific trap this closes: singular `problem` is writable because it
 * was live-verified `getType() === "textarea"`. That justification leans on the
 * type read, so the type read must keep the power to refuse the structured case.
 *
 * Widen this set per key, with live evidence for that key. Not per widget type.
 */
export const MULTILIST_WRITABLE_KEYS: ReadonlySet<string> = new Set(["instr"]);

/**
 * `pe` and `ros` are writable, and they are the ONLY two sections that do not
 * take a plain bullet write. Their content lives in a per-system checklist
 * (`textarea[name="pe-Resp"]`, 16 systems for `pe`, 17 for `ros`) which a bullet
 * does NOT merge into and `getJsonData()` never sees — so a whole review of
 * systems written as one bullet arrived as ONE BLOB alongside the template's
 * per-system fields, which is what the product owner reported.
 *
 * `stageInPage` therefore takes a checklist branch for these two keys and writes
 * each system into its own field ({@link planChecklistWrite},
 * {@link mergeChecklistFinding}), falling back to the bullet only for lines with
 * no field. Two live-verified hazards shape that branch:
 *
 *  1. An EMPTY system's row is `display: none` (`pe-GU`, `pe-Lymph`; 14 of 16
 *     rendered), and a DOM write into it persists across a full chart reload
 *     while staying invisible in the editor — signable, unseen content. The
 *     branch measures the row, tries Elation's "Show all checklist fields"
 *     trigger and then the inline `display`, and REFUSES the field if it is
 *     still hidden, routing that finding to the visible list entry instead.
 *  2. The fields arrive PRE-FILLED with the template's normals, indistinguishable
 *     from a physician's own edit. The write APPENDS on a new line and never
 *     replaces.
 *
 * Don't re-add a key block for these from a synchronous DOM measurement: the
 * listeditor's `<ol>` renders on save/refresh, so a zero-entry read straight
 * after a write means "not yet", not "never". Measure after a save/refresh, or
 * measure the model.
 */

// ── Per-system checklist sections (ros / pe) ─────────────────────────────────

/**
 * Elation's per-system checklist fields for the REVIEW OF SYSTEMS section.
 *
 * LIVE-VERIFIED on a sandbox SOAP chart (2026-08-05/06): the section's content
 * does not live in the model — `ros.getJsonData()` returns only the section's
 * `<ol>` entries and is `null` when there are none — it lives in 17
 * `textarea[name="ros-<System>"]` fields, each inside a `div.checklist-item`
 * whose `<label>` carries the display name, under `span#ros-detail-checklist-form`.
 *
 * The NAMES here are Elation's, exactly as they appear in the `name` attribute.
 * Case matters for the write (`ros-Resp`, not `ros-resp`).
 */
export const ROS_CHECKLIST_SYSTEMS: readonly string[] = [
  "General", "Diet", "Exercise", "Eyes", "HENT", "Resp", "CVS", "Breast",
  "GI", "GU", "Gyne", "MSS", "NS", "Skin", "Hemo", "Endoc", "Psych",
];

/** Same shape for the PHYSICAL EXAM section: 16 `textarea[name="pe-<System>"]`. */
export const PE_CHECKLIST_SYSTEMS: readonly string[] = [
  "General", "Eyes", "HENT", "Neck", "Resp", "CVS", "Breast", "Abdom",
  "GU", "MSS", "NS", "Skin", "Lymph", "Psych", "Pelvic", "Rectal",
];

/**
 * Clinical system name -> Elation checklist system name(s).
 *
 * ENCODED AS STRINGS (`"alias>Target"`, or `"alias>First,Second"`) on purpose:
 * `stageInPage` is serialized by `executeScript` and must inline its own copy of
 * this table, and a flat array of string literals is the one shape the parity
 * test can extract from a function's source and compare as a set. A nested
 * object literal would drift silently.
 *
 * The alias key is the label NORMALIZED — lowercased, every run of
 * non-alphanumerics collapsed to a single space, trimmed. So the library's
 * "Ears, nose, mouth, throat" is looked up as `ears nose mouth throat`.
 *
 * Candidates are ordered because the same clinical word lands on a DIFFERENT
 * Elation field per section: gastrointestinal is `ros-GI` but `pe-Abdom`, and
 * haematologic is `ros-Hemo` but `pe-Lymph`. Resolution takes the first
 * candidate that exists on the section being written.
 *
 * DELIBERATELY UNMAPPED, and this is not an oversight:
 *  - `extremities` (a physical-exam template system) — Elation's exam has no
 *    extremities field, and its normal finding ("no oedema, pulses intact") is
 *    vascular as much as musculoskeletal. Filing it under `pe-MSS` would assert
 *    a placement the clinician never chose.
 *  - `allergic and immunologic` (a review-of-systems template system) — Elation's
 *    ROS checklist has no equivalent field at all.
 * Both fall through to the section's list entry (a bullet), where they are
 * visible and attributed, rather than being guessed into a neighbouring system.
 */
export const CHECKLIST_SYSTEM_ALIASES: readonly string[] = [
  "constitutional>General",
  "nutrition>Diet",
  "activity>Exercise",
  "ophthalmologic>Eyes",
  "vision>Eyes",
  "visual>Eyes",
  "heent>HENT",
  "ent>HENT",
  "ears nose mouth throat>HENT",
  "ears nose and throat>HENT",
  "ear nose throat>HENT",
  "head eyes ears nose throat>HENT",
  "otolaryngologic>HENT",
  "throat>HENT",
  "respiratory>Resp",
  "pulmonary>Resp",
  "lungs>Resp",
  "chest>Resp",
  "cardiovascular>CVS",
  "cardiac>CVS",
  "heart>CVS",
  "cv>CVS",
  "breasts>Breast",
  "gastrointestinal>GI,Abdom",
  "abdomen>Abdom,GI",
  "abdominal>Abdom,GI",
  "genitourinary>GU",
  "urinary>GU",
  "genital>GU",
  "gynecologic>Gyne",
  "gynaecologic>Gyne",
  "gynecological>Gyne",
  "musculoskeletal>MSS",
  "msk>MSS",
  "neurologic>NS",
  "neurological>NS",
  "neuro>NS",
  "integumentary>Skin",
  "dermatologic>Skin",
  "skin and breast>Skin",
  "hematologic>Hemo,Lymph",
  "haematologic>Hemo,Lymph",
  "hematologic and lymphatic>Hemo,Lymph",
  "haematologic and lymphatic>Hemo,Lymph",
  "heme>Hemo",
  "lymphatic>Lymph,Hemo",
  "lymph nodes>Lymph,Hemo",
  "endocrine>Endoc",
  "psychiatric>Psych",
  "psychological>Psych",
  "mental status>Psych",
  "rectum>Rectal",
  "pelvis>Pelvic",
];

/**
 * The Elation checklist systems for a section key, or null when the section has
 * no per-system checklist (every other section on every template seen).
 */
export function checklistSystemsFor(sectionKey: string): readonly string[] | null {
  const norm = String(sectionKey ?? "").toLowerCase().replace(/[\s_-]+/g, "");
  if (norm === "ros") return ROS_CHECKLIST_SYSTEMS;
  if (norm === "pe") return PE_CHECKLIST_SYSTEMS;
  return null;
}

/**
 * Resolve one clinical system label to the Elation checklist system name on that
 * section, or null when nothing matches. Case/punctuation-insensitive, and an
 * Elation name resolves to itself so a round trip through the read side works.
 */
export function resolveChecklistSystem(
  sectionKey: string,
  label: string,
): string | null {
  const systems = checklistSystemsFor(sectionKey);
  if (!systems) return null;
  const norm = String(label ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  if (!norm) return null;
  const squashed = norm.replace(/\s+/g, "");
  for (const s of systems) if (s.toLowerCase() === squashed) return s;
  for (const entry of CHECKLIST_SYSTEM_ALIASES) {
    const cut = entry.indexOf(">");
    if (cut < 0) continue;
    if (entry.slice(0, cut) !== norm) continue;
    for (const cand of entry.slice(cut + 1).split(",")) {
      if (systems.indexOf(cand) !== -1) return cand;
    }
  }
  return null;
}

/** One planned per-system field write. */
export interface ChecklistFieldWrite {
  /** Elation checklist system name — the `name` suffix, e.g. "Resp". */
  system: string;
  /** The label as written in the draft, for messages. Never a finding. */
  label: string;
  /** The finding text destined for that system's textarea. */
  text: string;
}

export interface ChecklistWritePlan {
  entries: ChecklistFieldWrite[];
  /**
   * Lines that resolved to no system (an unmapped system, prose, a heading).
   * These are NOT dropped — the caller writes them as the section's list entry.
   */
  leftovers: string[];
}

/**
 * Split a drafted section body into per-system field writes.
 *
 * THIS IS THE FIX for "every item lands in one field". The drafted body is one
 * `Label: Finding` line per system (`renderTemplate` in `@astrana/ui/scribe`,
 * optionally `- ` bulleted by `proposeSectionAssignments`), and the old write
 * pushed the whole blob into a single `_model.addBullet` entry.
 *
 * Returns null when the section has no checklist — the caller then keeps the
 * legacy single-entry write, which is correct for every other section.
 *
 * A line with no resolvable `Label:` prefix becomes a leftover rather than being
 * glued onto the previous system: attaching continuations would silently file an
 * unrelated closing line ("otherwise negative") under whichever system happened
 * to come last. The ONE exception is an explicit bare header (`Respiratory:` on
 * its own line) — there the physician said which system the following lines
 * describe, so they are filed under it.
 */
export function planChecklistWrite(
  sectionKey: string,
  text: string,
): ChecklistWritePlan | null {
  const systems = checklistSystemsFor(sectionKey);
  if (!systems) return null;
  const bySystem = new Map<string, ChecklistFieldWrite>();
  const leftovers: string[] = [];
  const add = (system: string, label: string, finding: string): void => {
    const held = bySystem.get(system);
    if (held) held.text = held.text + "\n" + finding;
    else bySystem.set(system, { system, label, text: finding });
  };
  // A bare `Respiratory:` header attributes the lines BENEATH it. Remembered
  // here so those lines keep their system instead of being dropped or filed as
  // prose. THREE THINGS END ITS SCOPE, and all three matter clinically:
  // a BLANK LINE (the paragraph the physician started under the header ended),
  // the next LABELLED line (`Cardiovascular: RRR` names its own system), and a
  // bare heading that names NO system (`Assessment:`). Without the blank line,
  // `All other systems reviewed and negative.` — a whole-exam negation two
  // paragraphs below a `Respiratory:` header — was asserted as a respiratory
  // finding in a field a physician signs.
  let headerSystem: string | null = null;
  let headerLabel = "";
  for (const rawLine of String(text ?? "").split(/\r?\n/)) {
    // NEVER MUTATE THE PHYSICIAN'S TEXT. Only a real list marker comes off —
    // one leading `-`/`*`/`•`/`·` or `1.`/`1)` FOLLOWED BY A SPACE, which is
    // list formatting the renderer added. The old class-based strip
    // (`/^[\s>*•·-]+/`) ate clinical glyphs: `>2 pillow orthopnea` became
    // `2 pillow orthopnea`, `>=2+ edema` became `=2+ edema`, and `-5 lbs`
    // became `5 lbs` — a weight LOSS charted as a gain.
    const line = rawLine.replace(/^\s*(?:[-*•·]|\d+[.)])\s+/, "").trim();
    if (!line) {
      // A blank line ends the header's paragraph, so what follows is no longer
      // that system's findings.
      headerSystem = null;
      headerLabel = "";
      continue;
    }
    const bare = line.match(/^([^:]{1,60}):$/);
    if (bare) {
      // A bare `Something:` line is a HEADING ("Review of Systems:") only when
      // it names no system — kept as a leftover it became a visible list entry
      // INSIDE the section it names, junk in a note a physician signs. When it
      // DOES name a system it is a system header, and dropping it deleted a
      // line of the draft and the attribution of everything under it.
      const named = resolveChecklistSystem(sectionKey, bare[1]);
      headerSystem = named;
      headerLabel = named ? bare[1].trim() : "";
      continue;
    }
    const m = line.match(/^([^:]{1,60}):\s*(\S[\s\S]*)$/);
    const system = m ? resolveChecklistSystem(sectionKey, m[1]) : null;
    if (!m || !system) {
      // Under a system header the line belongs to that system. Otherwise it is
      // prose or an unmapped system: a leftover, never glued onto whichever
      // labelled system happened to come last.
      if (headerSystem) add(headerSystem, headerLabel, line);
      else leftovers.push(line);
      continue;
    }
    headerSystem = null;
    headerLabel = "";
    add(system, m[1].trim(), m[2].trim());
  }
  // Emit in the section's own field order, so a write is deterministic and reads
  // top-to-bottom the way the checklist is rendered.
  const entries = systems
    .map((s) => bySystem.get(s))
    .filter((e): e is ChecklistFieldWrite => !!e);
  return { entries, leftovers };
}

/**
 * APPEND, NEVER REPLACE — the whole hazard-2 decision, in one pure function.
 *
 * Elation's checklist fields arrive PRE-FILLED with the template's normals
 * (live: `pe-General` = "NAD, Alert, not well-appearing. Vital signs noted."),
 * and by the time a clinician writes back, some of those may be findings they
 * edited themselves. There is no way to tell a template default apart from a
 * charted finding by looking at the field, so replacing would sometimes delete a
 * physician's own documentation with no trace. This returns the existing value
 * with the new finding on a NEW LINE beneath it.
 *
 * Returns null when there is nothing to do — empty finding, or every line of it
 * is ALREADY A LINE of the field (so pressing write twice does not duplicate a
 * line).
 *
 * The already-present test is LINE-WISE, not `indexOf`. A substring test threw
 * findings away: the pre-filled normals are long negation sentences (live:
 * `pe-Resp` = "No wheezes, no rales, no rhonchi"), so a real finding like
 * "No rales" was reported "already present" and silently discarded — the field
 * kept the template default and the write claimed success. A finding is only
 * already documented when it is one of the field's own lines.
 */
export function mergeChecklistFinding(
  existing: string,
  finding: string,
): string | null {
  const add = String(finding ?? "").trim();
  if (!add) return null;
  const cur = String(existing ?? "");
  if (!cur.trim()) return add;
  const have: string[] = [];
  for (const line of cur.split(/\r?\n/)) {
    const t = line.trim().toLowerCase();
    if (t) have.push(t);
  }
  const fresh: string[] = [];
  for (const line of add.split(/\r?\n/)) {
    const t = line.trim();
    if (!t) continue;
    if (have.indexOf(t.toLowerCase()) !== -1) continue;
    have.push(t.toLowerCase());
    fresh.push(t);
  }
  if (!fresh.length) return null;
  return cur.replace(/\s+$/, "") + "\n" + fresh.join("\n");
}

/**
 * Pure, testable writability check for a section key. Normalizes the key
 * (lowercase, strip spaces/underscores/hyphens) and returns false for any key
 * in {@link STRUCTURED_SECTION_KEYS}.
 *
 * KEY-ONLY, and therefore NOT the whole gate: it cannot see the live section,
 * so it knows nothing about {@link STRUCTURED_SECTION_TYPES} or
 * {@link MULTILIST_WRITABLE_KEYS}. The MAIN-world read applies the type rules
 * on top, and `stageInPage` re-checks both defensively so a structured write
 * can never land. Do not reason about writability from this function alone.
 */
export function isSectionKeyWritable(key: string): boolean {
  const norm = String(key ?? "").toLowerCase().replace(/[\s_-]+/g, "");
  return (
    !STRUCTURED_SECTION_KEYS.has(norm) &&
    !POLICY_EXCLUDED_SECTION_PATTERN.test(norm)
  );
}

/** Which note engine is open, deciding whether per-section boxes are shown. */
export type NoteEngine = "legacy" | "vn2" | "nonvisit" | "none";

/**
 * Why a section read could not be performed.
 *
 * The distinction is load-bearing, and it is the one thing a failed read CAN
 * say about itself. `resolveSectionInventory`'s carry-forward rests on the
 * argument that a failed read cannot name the note it failed on — true of a
 * transport blip, but NOT true of a patient mismatch, where the page told us
 * both ids and they were different charts.
 *
 *  - `"patient-mismatch"` — the page reported a patient id and it was NOT the
 *    one the snapshot being enriched belongs to. The read knows this is the
 *    wrong chart. Nothing remembered from the other chart may be published.
 *  - `"unreadable"` — everything else, and the transient blips the
 *    carry-forward exists for: an `executeScript` throw, a malformed result,
 *    `el8.appchart` momentarily falsy, the outer catch, and a patient guard
 *    that refused because the page reported NO patient id at all (Elation
 *    between URLs — identity unknown, not identity wrong).
 */
export type NoteReadFailure = "patient-mismatch" | "unreadable";

/** Everything a section read reports, whether or not it succeeded. */
interface NoteSectionsReport {
  engine: NoteEngine;
  sections: EhrNoteSection[];
  /**
   * Id of the note open RIGHT NOW, read from the app object.
   *
   * The content-script scrape cannot get this. It looks for hidden inputs
   * (`id`/`noteId`/`visitNoteId`) inside `#note-new-visit-form`, and live
   * verification on 2026-08-05 found the form present with NONE of those
   * inputs — so `openNote.noteId` was `undefined`, and the host's fail-closed
   * write guard ("Open a note in Elation before writing.") refused EVERY
   * write. The scrape's only other source is `window.el8`, which does not
   * exist in the isolated world it runs in.
   *
   * This read already runs in the MAIN world, where `el8` is real, so the id
   * rides along on it rather than needing a second injection.
   */
  noteId?: string;
  /**
   * The OPEN note's template display name ("Simple Note", "SOAP Note", …), from
   * `getCurrFormTemplate().displayName` — live-verified 2026-08-05. Read here
   * because this is already the per-poll MAIN-world read of the open note, and
   * nothing else on the page states which note type is open (the panel used to
   * have no signal at all and defaulted to a Simple Note). Absent when no
   * legacy note is open or the template read failed.
   */
  templateName?: string;
}

/**
 * Result of the MAIN-world section read (`readNoteSectionsInPage`).
 *
 * `ok` distinguishes "the read RAN and this is what the chart says" from "the
 * read could not be performed". `true` with `engine: "none"` is an entirely
 * legitimate answer: no editable note is open (signed, closed, or between
 * notes). Callers MUST NOT conflate the two — a signed note and a transport
 * blip used to return the byte-identical `{engine:"none", sections:[]}`, so
 * `resolveSectionInventory` masked a signed note with the last open note's
 * writable sections and kept offering them.
 *
 * A union rather than an optional field so tsc forces every failure path to say
 * WHY, the same way `ok` forces every path to say whether it ran at all. Both
 * variants carry the same reported fields, so existing destructuring is
 * unaffected.
 */
export type NoteSectionsResult =
  | ({ ok: true } & NoteSectionsReport)
  | ({ ok: false; failure: NoteReadFailure } & NoteSectionsReport);

/**
 * Title-case a lowercase/cryptic section key for display when `getName()` gave
 * nothing better. Already-pretty labels (mixed case / containing a space) pass
 * through untouched, so a real `getName()` like "Follow-up" is preserved.
 * A couple of well-known Elation keys get friendlier names.
 */
export function prettifySectionLabel(label: string | undefined, key: string): string {
  const raw = (label ?? "").trim();
  // A real getName() is usually already presentable — keep it if it has any
  // uppercase or whitespace (i.e. it isn't a bare lowercase key).
  if (raw && (/[A-Z]/.test(raw) || /\s/.test(raw))) return raw;

  const source = raw || key || "";
  const known: Record<string, string> = {
    assessplan: "Assessment & Plan",
    hpi: "HPI",
    ros: "ROS",
    pe: "Physical Exam",
    followup: "Follow-up",
    instr: "Instructions",
  };
  const lower = source.toLowerCase();
  if (known[lower]) return known[lower];

  // Split camelCase / snake / kebab, then Title Case each word.
  const words = source
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return source;
  return words
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Defensively normalize the raw section list from the injected MAIN-world read
 * (which crosses the executeScript boundary and could carry malformed entries).
 * Drops entries without a string `key`; coerces `label`/`currentText` to strings.
 */
export function normalizeSections(raw: unknown): EhrNoteSection[] {
  if (!Array.isArray(raw)) return [];
  const out: EhrNoteSection[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    if (typeof rec.key !== "string" || !rec.key) continue;
    // Writability is gated by BOTH the MAIN-world read's best-effort flag
    // (an explicit `false` from the live `getType()` read is honored) AND the
    // pure key blocklist as a backstop — a structured key is never writable
    // even if the read misjudged it.
    const writable = rec.writable !== false && isSectionKeyWritable(rec.key);
    out.push({
      key: rec.key,
      label: typeof rec.label === "string" ? rec.label : rec.key,
      currentText: typeof rec.currentText === "string" ? rec.currentText : "",
      writable,
    });
  }
  return out;
}

/**
 * Build a `WriteBackRequest` for a single section's add-box. STAGE targets the
 * exact `_inputSections` key (no `mapReferenceFn`); COMMIT gets the Capitalized
 * key as `category` (best-effort — different vocabulary; see the design doc).
 */
export function buildSectionWriteRequest(args: {
  patientId: string;
  noteId: string;
  sectionKey: string;
  text: string;
  mode: WriteBackMode;
  /** {@link WriteBackRequest.linesJson} — `""`/absent means no provenance. */
  linesJson?: string;
}): WriteBackRequest {
  const cap = args.sectionKey.charAt(0).toUpperCase() + args.sectionKey.slice(1);
  return {
    patientId: args.patientId,
    noteId: args.noteId,
    category: cap,
    sectionKey: args.sectionKey,
    text: args.text,
    mode: args.mode,
    noteState: "open",
    ...(args.linesJson ? { linesJson: args.linesJson } : {}),
  };
}

// ── Commit transport (REST, isolated world) ─────────────────────────────────

function fail(errorCode: WriteBackErrorCode, status = 0, detail?: string): WriteBackResult {
  return { ok: false, status, readAfterWrite: false, errorCode, detail };
}

async function commitWriteBack(
  req: WriteBackRequest,
  deps: WriteBackDeps,
): Promise<WriteBackResult> {
  const csrf = readCsrfToken(deps.cookie);
  if (!csrf) return fail("CSRF_MISSING");

  let res: Response;
  try {
    res = await deps.fetchFn(
      `/patient/${req.patientId}/visit-notes/${req.noteId}/bullet/`,
      {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "x-requested-with": "XMLHttpRequest",
          "x-csrftoken": csrf,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: buildBulletFormBody(req),
      },
    );
  } catch (e) {
    return fail("HTTP_5XX", 0, String(e));
  }

  const body = await res.text();
  if (!res.ok) {
    return fail(res.status >= 500 ? "HTTP_5XX" : "HTTP_4XX", res.status, body.slice(0, 400));
  }

  // Capture the created bullet id from the POST response (`{"id":<n>,...}`),
  // parsed defensively — a malformed/missing id just disables the id-based
  // check below and falls back to the text-based one.
  let createdId: number | null = null;
  try {
    const parsed: unknown = JSON.parse(body);
    if (
      parsed &&
      typeof parsed === "object" &&
      "id" in parsed &&
      typeof (parsed as { id: unknown }).id === "number"
    ) {
      createdId = (parsed as { id: number }).id;
    }
  } catch {
    createdId = null;
  }

  let readAfterWrite = false;
  try {
    const chk = await deps.fetchFn(`/patient/${req.patientId}/items/?id[]=${req.noteId}`, {
      credentials: "same-origin",
    });
    const chkBody = await chk.text();
    // Prefer confirming the created id shows up in the items response — this
    // is robust to multiline/unicode/quote content, which the naive
    // `.includes(req.text)` mis-reports because the endpoint returns
    // JSON-escaped text. Fall back to a JSON-escaped text comparison when we
    // don't have an id to check (e.g. POST response didn't parse).
    if (createdId != null) {
      readAfterWrite = chkBody.includes(`"id":${createdId}`);
    } else {
      // NOTE: the text-based fallback only confirms the text is *present*
      // somewhere in the note items — NOT that *this* write is what landed
      // (an identical prior bullet would also match). The id-based branch
      // above is the authoritative confirmation; this is best-effort.
      readAfterWrite = chkBody.includes(JSON.stringify(req.text).slice(1, -1));
    }
  } catch {
    readAfterWrite = false;
  }

  // A 2xx POST means the bullet IS persisted. Returning ok:false here would
  // invite a retry that duplicates the bullet (writes are irreversible without
  // a separate delete — spec guardrail). So success is anchored on the 2xx;
  // `readAfterWrite` merely records whether the verify GET could confirm it.
  return {
    ok: true,
    status: res.status,
    readAfterWrite,
    mode: "commit",
  };
}

/**
 * COMMIT transport entry (content-script isolated world). Shared guards +
 * REST POST + read-after-write. `stage` is NOT handled here — it runs in the
 * MAIN world via `stageInPage`; routing to it is the sidepanel's job. If called
 * with mode:"stage" this returns STAGE_NOT_IMPLEMENTED and never POSTs.
 */
export async function performWriteBack(
  req: WriteBackRequest,
  deps: WriteBackDeps,
): Promise<WriteBackResult> {
  if (!req.text || !req.text.trim()) return fail("EMPTY_TEXT");
  // Signed-note protection is primarily UI-gated today (the panel sets
  // `noteState` from what it observes in the page); this check is a backstop
  // in the adapter, not the sole line of defense.
  if (req.noteState === "signed") return fail("SIGNED_NOTE_UNSUPPORTED");
  if (req.mode === "stage") return fail("STAGE_NOT_IMPLEMENTED");
  if (!req.noteId) return fail("NO_OPEN_NOTE");
  return commitWriteBack(req, deps);
}

// ── Stage transport (MAIN world, injected) ──────────────────────────────────

/**
 * Result shape returned by `stageInPage` (a subset of WriteBackResult built in
 * the page world, where our types aren't available). The sidepanel widens it.
 */
export interface StageInPageResult {
  ok: boolean;
  engine: string;
  section?: string;
  errorCode?: string;
  detail?: string;
  /**
   * The write succeeded but the per-system fix did NOT fully apply: at least one
   * system the draft named had no usable Elation field on this page, so its
   * finding went into the section's list entry instead of its own field. The
   * per-system `name` suffixes are PRACTICE CONFIGURATION, so a practice whose
   * checklist is named differently degrades all the way back to the originally
   * reported "one blob" behaviour — which without this flag is indistinguishable
   * from success. Callers should surface it, not just log it.
   */
  degraded?: boolean;
}

/**
 * Injected into the Elation tab's MAIN world via
 * `chrome.scripting.executeScript({ world: "MAIN", func: stageInPage, args: [text, preferredRefs] })`.
 *
 * MUST be fully self-contained — executeScript serializes it, so it cannot
 * reference anything outside its own body (no imports, no module-scope consts).
 * The pure helpers above are duplicated inline here; keep them in sync.
 *
 * Contract (verified live, findings ACT-1242):
 *  - Primitive: `window.el8.appchart.mgr.notes.addText(text, referenceName)`.
 *    NEVER raw `_model.addBullet` (leaves orphaned bullets → note corruption).
 *  - MANDATORY guard: resolve `key = getCurrFormTemplate().mapReferenceFn(ref)`
 *    and confirm `_inputSections[key]` exists BEFORE addText.
 *  - Empty-text guard (addText("") appends a junk blank line).
 *  - Engine detection: legacy fully supported; VN2 + nonvisit → ENGINE_UNSUPPORTED.
 */
export function stageInPage(
  text: string,
  preferredRefs: string[],
  expectedPatientId?: string,
  sectionKey?: string,
  /**
   * Id of the note the draft was written for. When set, the write is refused
   * unless the note open in the page RIGHT NOW has this id. See the note-match
   * guard below for why the host's own check is not sufficient.
   */
  expectedNoteId?: string,
  /**
   * {@link WriteBackRequest.linesJson} — the drafter's per-system provenance,
   * as a JSON string because executeScript can only hand this function strings.
   * `""`/absent falls back to parsing `text`. Decoded by the INLINE reader in
   * the checklist branch below (this function cannot import
   * `decodeSectionLines`).
   */
  linesJson?: string,
): StageInPageResult {
  try {
    // ── Patient-match guard ───────────────────────────────────────────
    // Runs in the page world BEFORE any addText — read the live chart's
    // patient id straight from the URL and refuse if it isn't the patient
    // the panel acted on. This is the last line of defense against a write
    // landing in the wrong chart (e.g. the tab navigated after selection).
    // Self-contained (no external refs — executeScript serializes this fn).
    const livePatientId = location.pathname.match(/\/patient\/(\d+)/)?.[1];
    if (expectedPatientId && livePatientId !== expectedPatientId) {
      return {
        ok: false,
        engine: "unknown",
        errorCode: "PATIENT_MISMATCH",
        detail: `tab shows patient ${livePatientId ?? "none"}, expected ${expectedPatientId}`,
      };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const el8: any = (window as any).el8;
    if (!el8?.appchart?.mgr?.notes?.addText) {
      return { ok: false, engine: "none", errorCode: "EL8_UNAVAILABLE", detail: "window.el8.appchart.mgr.notes.addText missing" };
    }

    // Empty-text guard (addText("") appends a junk blank line).
    if (!text || !text.trim()) {
      return { ok: false, engine: "unknown", errorCode: "EMPTY_TEXT" };
    }

    const chart = el8.appchart;

    // ── Engine detection ──────────────────────────────────────────────
    const legacyOpen = !!chart.visitNote?.main?.isOpen?.();
    const nonvisitOpen = !!chart.nonvisitNote?.main?.isOpen?.();
    const vn2Open = !!chart.visitNote2?.main?.isOpen?.();
    const engine = legacyOpen ? "legacy" : nonvisitOpen ? "nonvisit" : vn2Open ? "vn2" : "none";

    if (engine === "none") {
      return { ok: false, engine, errorCode: "NO_OPEN_NOTE", detail: "no note engine reports isOpen()" };
    }

    // ── Note-match guard ──────────────────────────────────────────────
    // The ONLY note-identity check that cannot go stale. The host's copy is a
    // snapshot of a message that arrives on a ~10s poll, so after a clinician
    // signs note A and opens note B on the SAME patient there is a window where
    // the host still believes A is open — it then compares A against A, agrees,
    // and the write lands in B. Wrong encounter, wrong date of service, no
    // error shown. The patient guard above does not catch it (same patient) and
    // the engine check does not either (a note IS open).
    //
    // Read the id here, in the page, at the moment of writing.
    if (expectedNoteId) {
      let liveNoteId = "";
      try {
        const raw = chart.visitNote?.main?.getNoteId?.();
        liveNoteId = raw == null ? "" : String(raw);
      } catch {
        /* accessor absent/threw — fall through to the mismatch check below */
      }
      // Refuse when the live id is unreadable too: the caller asked for a
      // specific note, and "I cannot tell" is not "yes". Fail closed.
      if (liveNoteId !== expectedNoteId) {
        return {
          ok: false,
          engine,
          errorCode: "OPEN_NOTE_MISMATCH",
          detail: `open note is ${liveNoteId || "unreadable"}, draft was written for ${expectedNoteId}`,
        };
      }
    }
    // THIS BUILD: legacy only. VN2 is being live-verified separately.
    // EXTENSION POINT (VN2): when a VN2 note is available, branch here and
    // fire `el8React.messenger.send("VisitNote2TextSync", { type, payload:
    // { text, target } })` — target vocabulary differs from _inputSections
    // keys (findings "Two engines exist"). Until then, refuse cleanly.
    if (engine !== "legacy") {
      return { ok: false, engine, errorCode: "ENGINE_UNSUPPORTED", detail: `engine '${engine}' not supported in this build` };
    }

    const m = chart.visitNote.main;

    // ── EXACT-KEY WRITE (ACT-1242 fix) ────────────────────────────────
    // When the caller supplies a `sectionKey` (an exact `_inputSections` key
    // read from THIS open note), bypass `mapReferenceFn` entirely — no fuzzy
    // intent→section mapping means no section-mismapping bug. Replicate
    // `addText`'s internals with the exact key, keeping the corruption-guard
    // discipline: confirm the section exists first, and NEVER `addBullet`
    // without the paired `addEntry`.
    if (sectionKey) {
      // Defensive backstop (bug 2): NEVER free-text write into a structured
      // list/codes section. The UI already hides these, but re-check in the
      // page world so a stale or malformed request can't slip a structured
      // write through. Inline blocklist mirrors STRUCTURED_SECTION_KEYS
      // (self-contained fn).
      const structuredKeys = [
        "order", "orders", "referenced", "problems", "problemlist",
        "med", "meds", "medication", "medications", "medlist", "medicationlist",
        "allergy", "allergies", "allergylist", "vital", "vitals", "data",
        "cpt", "cptcode", "cptcodes", "dx", "dxcode", "dxcodes",
        "diagnosis", "diagnoses", "prescription", "prescriptions",
        "referral", "referrals", "lab", "labs", "imaging",
        "immunization", "immunizations", "vaccine", "vaccines",
      ];
      const normKey = sectionKey.toLowerCase().replace(/[\s_-]+/g, "");
      if (structuredKeys.indexOf(normKey) !== -1) {
        return {
          ok: false,
          engine,
          section: sectionKey,
          errorCode: "SECTION_NOT_WRITABLE",
          detail: `section '${sectionKey}' is a structured list/codes section — free-text write refused (would corrupt note)`,
        };
      }
      // Distinct reason, distinct message. Saying "would corrupt note" here
      // would be FALSE — a procedures list is a listeditor and accepts the
      // write fine; it is simply the wrong home for clinical prose. This detail
      // is console-logged on every failed write, so a wrong one misleads
      // whoever debugs the next report.
      if (/procedure/.test(normKey)) {
        return {
          ok: false,
          engine,
          section: sectionKey,
          errorCode: "SECTION_NOT_WRITABLE",
          detail: `section '${sectionKey}' is a procedures list — free-text write refused (wrong destination for prose, not a corruption risk)`,
        };
      }
      const sectionsMap = m._inputSections;
      if (
        !sectionsMap ||
        !Object.prototype.hasOwnProperty.call(sectionsMap, sectionKey) ||
        !sectionsMap[sectionKey]
      ) {
        return {
          ok: false,
          engine,
          section: sectionKey,
          errorCode: "SECTION_UNRESOLVED",
          detail: `section '${sectionKey}' is not a live section on this note — refusing (would corrupt note)`,
        };
      }
      // Live-type backstop (bug 2): even for a non-blocklisted key, refuse if
      // the live section reports a type that cannot hold a free-text bullet.
      // Writable: "textarea", "listeditor", and "multilist" ONLY for the keys
      // proven live. Mirrors STRUCTURED_SECTION_TYPES / MULTILIST_WRITABLE_KEYS
      // — same array-literal shape, because the parity test extracts them by
      // name from this function's source.
      const structuredTypes = ["textlist"];
      const multilistWritableKeys = ["instr"];
      try {
        const liveSec = sectionsMap[sectionKey];
        const liveType =
          typeof liveSec.getType === "function" ? String(liveSec.getType()).toLowerCase() : "";
        if (structuredTypes.indexOf(liveType) !== -1) {
          return {
            ok: false,
            engine,
            section: sectionKey,
            errorCode: "SECTION_NOT_WRITABLE",
            detail: `section '${sectionKey}' is a document-reference list (${liveType}) — free-text write refused`,
          };
        }
        // A multilist accepts the write mechanically, so the type alone cannot
        // say whether it SHOULD receive prose — a structured problem/goals list
        // is a multilist too. Only keys with live evidence are allowed.
        if (liveType === "multilist" && multilistWritableKeys.indexOf(normKey) === -1) {
          return {
            ok: false,
            engine,
            section: sectionKey,
            errorCode: "SECTION_NOT_WRITABLE",
            detail: `section '${sectionKey}' is a structured list (multilist) not verified for free-text write — refused`,
          };
        }
      } catch {
        /* getType absent/threw — inconclusive; the blocklist above still guards */
      }

      // ── PER-SYSTEM CHECKLIST WRITE (ros / pe) ─────────────────────────
      // THE FIX for "every item lands in one field". `ros` and `pe` keep their
      // content in per-system textareas (`textarea[name="ros-Resp"]`, 17 systems
      // for ros / 16 for pe) that a `_model.addBullet` entry does NOT merge into
      // — so the old write put the whole review of systems into one list entry,
      // which is exactly what was reported. Write each system into its own field
      // and keep the bullet only for what has no field.
      //
      // Inlined mirrors of ROS_CHECKLIST_SYSTEMS / PE_CHECKLIST_SYSTEMS /
      // CHECKLIST_SYSTEM_ALIASES (executeScript serializes this function, so it
      // cannot reference them). Same array-literal shape — the parity test
      // extracts them from this source by name.
      const rosSystems = [
        "General", "Diet", "Exercise", "Eyes", "HENT", "Resp", "CVS", "Breast",
        "GI", "GU", "Gyne", "MSS", "NS", "Skin", "Hemo", "Endoc", "Psych",
      ];
      const peSystems = [
        "General", "Eyes", "HENT", "Neck", "Resp", "CVS", "Breast", "Abdom",
        "GU", "MSS", "NS", "Skin", "Lymph", "Psych", "Pelvic", "Rectal",
      ];
      const systemAliases = [
        "constitutional>General",
        "nutrition>Diet",
        "activity>Exercise",
        "ophthalmologic>Eyes",
        "vision>Eyes",
        "visual>Eyes",
        "heent>HENT",
        "ent>HENT",
        "ears nose mouth throat>HENT",
        "ears nose and throat>HENT",
        "ear nose throat>HENT",
        "head eyes ears nose throat>HENT",
        "otolaryngologic>HENT",
        "throat>HENT",
        "respiratory>Resp",
        "pulmonary>Resp",
        "lungs>Resp",
        "chest>Resp",
        "cardiovascular>CVS",
        "cardiac>CVS",
        "heart>CVS",
        "cv>CVS",
        "breasts>Breast",
        "gastrointestinal>GI,Abdom",
        "abdomen>Abdom,GI",
        "abdominal>Abdom,GI",
        "genitourinary>GU",
        "urinary>GU",
        "genital>GU",
        "gynecologic>Gyne",
        "gynaecologic>Gyne",
        "gynecological>Gyne",
        "musculoskeletal>MSS",
        "msk>MSS",
        "neurologic>NS",
        "neurological>NS",
        "neuro>NS",
        "integumentary>Skin",
        "dermatologic>Skin",
        "skin and breast>Skin",
        "hematologic>Hemo,Lymph",
        "haematologic>Hemo,Lymph",
        "hematologic and lymphatic>Hemo,Lymph",
        "haematologic and lymphatic>Hemo,Lymph",
        "heme>Hemo",
        "lymphatic>Lymph,Hemo",
        "lymph nodes>Lymph,Hemo",
        "endocrine>Endoc",
        "psychiatric>Psych",
        "psychological>Psych",
        "mental status>Psych",
        "rectum>Rectal",
        "pelvis>Pelvic",
      ];
      const checklistSystems =
        normKey === "ros" ? rosSystems : normKey === "pe" ? peSystems : null;

      if (checklistSystems) {
        // The section's own checklist form (`span#pe-detail-checklist-form`, the
        // verified live container). Resolved FIRST because it is the write's
        // CONTAINMENT SCOPE, not merely the bound on the reveal:
        //
        //  - The bullet half of this write goes through `m._model.addBullet` +
        //    `sectionsMap[sectionKey].addEntry`, i.e. the note whose id the guard
        //    above validated. An unscoped `document.querySelectorAll` for the
        //    per-system half is not tied to that note at all, and its
        //    last-match-wins indexing meant that with two checklist forms in the
        //    DOM (a draft alongside an amendment — `ptdata.visitNotes` includes
        //    drafts and an amendment KEEPS the note id, so `getNoteId()` cannot
        //    tell them apart) the two halves of one write could land in two
        //    different notes.
        //  - `getElementById` returns the FIRST match and ids are not guaranteed
        //    unique on a live SPA page, so two forms means we cannot say which
        //    one belongs to the validated note. That is refused, not guessed.
        //  - One form is not automatically THE note's form either. When the DOM
        //    exposes a note id on an ancestor and it disagrees with the id the
        //    guard above validated, the write is refused: a page rendering a
        //    single FOREIGN checklist form used to receive the write and report
        //    success.
        //
        // RE-RESOLVABLE ON DEMAND. Elation's own "Show all checklist fields"
        // handler may RE-RENDER the checklist, which detaches every node
        // captured before the click. A detached textarea still accepts
        // `el.value = …` silently, so a write reported `filled CVS` while the
        // field the clinician sees stayed empty. Every handle is therefore
        // re-resolved out of the live document, and nothing is reported as
        // written until it has been read back (`verifyWritten`).
        let checklistForm: HTMLElement | null = null;
        let formsFound = 0;
        let foreignNoteId = "";
        let fields: Record<string, HTMLTextAreaElement> = {};
        let fieldCount = 0;
        let duplicateSuffix = "";
        let lockedSuffix = "";
        // The note id the form's nearest note container carries, when the page
        // exposes one at all. "" means unknowable — not "matches".
        // Walks THROUGH `<body>` and `<html>`: stopping short of `<body>` made a
        // note id declared on the body element invisible, so a page that did say
        // which note it was showing was treated as "unknowable" and written to.
        const noteIdOf = (start: Element | null): string => {
          try {
            let node: Element | null = start;
            while (node) {
              const attr =
                typeof node.getAttribute === "function"
                  ? node.getAttribute("data-note-id")
                  : null;
              if (attr && String(attr).trim()) return String(attr).trim();
              node = node.parentElement;
            }
          } catch {
            /* unreadable — cannot verify, and we do not guess */
          }
          return "";
        };
        // Index the live fields by their `name` suffix. Scoped to the resolved
        // checklist form when there is one; only a page with no identifiable
        // form at all falls back to the whole document (and then a duplicate
        // suffix is refused below rather than resolved by document order).
        const indexChecklistFields = (): void => {
          checklistForm = null;
          formsFound = 0;
          foreignNoteId = "";
          try {
            const formNodes = document.querySelectorAll(
              '[id="' + normKey + '-detail-checklist-form"]',
            );
            formsFound = formNodes.length;
            if (formNodes.length === 1) {
              const candidate = formNodes[0] as HTMLElement;
              const owner = noteIdOf(candidate);
              if (expectedNoteId && owner && owner !== String(expectedNoteId)) {
                foreignNoteId = owner;
              } else {
                checklistForm = candidate;
              }
            }
          } catch {
            /* unqueryable — the document-wide fallback below still applies */
          }
          fields = {};
          fieldCount = 0;
          duplicateSuffix = "";
          lockedSuffix = "";
          try {
            const scope: ParentNode = checklistForm || document;
            const nodes = scope.querySelectorAll(
              'textarea[name^="' + normKey + '-"]',
            );
            for (let i = 0; i < nodes.length; i += 1) {
              const el = nodes[i] as HTMLTextAreaElement;
              const suffix = String(el.name || "").slice(normKey.length + 1);
              if (!suffix) continue;
              const slot = suffix.toLowerCase();
              if (fields[slot] && fields[slot] !== el) duplicateSuffix = suffix;
              fields[slot] = el;
              fieldCount += 1;
              // A locked control is not a writable one. `el.value = …` on a
              // `disabled` textarea succeeds in the DOM and is never submitted,
              // so without this the write reported `filled General, Resp` on a
              // signed or locked note while nothing persisted — a guaranteed
              // false success on exactly the note state where it must refuse.
              if (!lockedSuffix && (el.disabled || el.readOnly)) lockedSuffix = suffix;
              // THE NOTE-ID LINK IS NOT JUST THE ONE-FORM PATH'S. A page with no
              // identifiable checklist form falls back to the whole document,
              // and that scope had no note-id check at all: a chart showing a
              // FOREIGN note's `pe-Resp` with no `pe-detail-checklist-form`
              // around it was written to and reported `filled Resp`. Every field
              // we are about to write must belong to the validated note.
              if (!foreignNoteId && expectedNoteId) {
                const fieldOwner = noteIdOf(el);
                if (fieldOwner && fieldOwner !== String(expectedNoteId)) {
                  foreignNoteId = fieldOwner;
                }
              }
            }
          } catch {
            /* no checklist reachable — falls through to the single-entry write */
          }
        };
        indexChecklistFields();
        if (formsFound > 1) {
          return {
            ok: false,
            engine,
            section: sectionKey,
            errorCode: "SECTION_UNRESOLVED",
            detail:
              "found " +
              formsFound +
              " '" +
              normKey +
              "-detail-checklist-form' containers — cannot tell which belongs to the open note; refusing (a write could land in another note)",
          };
        }
        // The one form in the page belongs to a DIFFERENT note than the guard
        // validated. Refuse: the bullet half of this write goes to the
        // validated note via `addBullet`, so writing here would split one write
        // across two notes.
        if (foreignNoteId) {
          return {
            ok: false,
            engine,
            section: sectionKey,
            errorCode: "OPEN_NOTE_MISMATCH",
            detail:
              "the '" +
              normKey +
              "' checklist in the page belongs to note " +
              foreignNoteId +
              ", the draft was written for " +
              String(expectedNoteId) +
              "; nothing was written",
          };
        }
        if (fieldCount > 0 && duplicateSuffix) {
          return {
            ok: false,
            engine,
            section: sectionKey,
            errorCode: "SECTION_UNRESOLVED",
            detail:
              "two '" +
              normKey +
              "-" +
              duplicateSuffix +
              "' fields are in scope — cannot tell which belongs to the open note; refusing",
          };
        }
        // Any locked field means the note is locked: refuse the WHOLE write
        // loudly rather than writing part of it into a form that will not
        // persist. Fail-closed on purpose — a signed note is the one state where
        // a silent success is worst.
        if (fieldCount > 0 && lockedSuffix) {
          return {
            ok: false,
            engine,
            section: sectionKey,
            errorCode: "SIGNED_NOTE_UNSUPPORTED",
            detail:
              "checklist field '" +
              normKey +
              "-" +
              lockedSuffix +
              "' is disabled/readOnly — the note is signed or locked; nothing was written",
          };
        }

        // A template without a checklist (or a page state where the fields are
        // not in the DOM at all) keeps the legacy behaviour rather than losing
        // the text.
        if (fieldCount > 0) {
          // Hidden-row measurement. HAZARD 1, live-verified: an EMPTY system's
          // row is `display: none` (pe-GU, pe-Lymph; 14 of 16 rendered), and a
          // write into it PERSISTS across a full chart reload while staying
          // invisible in the editor — content a clinician could sign without
          // ever seeing it. Computed style rather than offsetParent/getClientRects
          // because those are the signal the live measurement used AND the only
          // one jsdom can reproduce, so the guard is testable.
          const isHidden = (el: Element): boolean => {
            try {
              let node: Element | null = el;
              while (node && node !== document.body) {
                const st = window.getComputedStyle
                  ? window.getComputedStyle(node)
                  : null;
                if (st && (st.display === "none" || st.visibility === "hidden")) {
                  return true;
                }
                node = node.parentElement;
              }
            } catch {
              /* cannot measure — treat as hidden, i.e. refuse to write blind */
              return true;
            }
            return false;
          };
          // Elation's own affordance for this: "Show all checklist fields".
          // Text-matched because no stable id/class for it was ever verified;
          // length-capped so a huge ancestor whose textContent merely CONTAINS
          // the phrase is never the thing that gets clicked.
          const clickShowAllChecklistFields = (): void => {
            try {
              const nodes = document.querySelectorAll("a,button,span,label,div");
              for (let i = 0; i < nodes.length; i += 1) {
                const node = nodes[i] as HTMLElement;
                const t = String(node.textContent || "").trim();
                if (t.length > 60 || !/show all checklist/i.test(t)) continue;
                if (typeof node.click === "function") node.click();
                return;
              }
            } catch {
              /* no trigger — the inline un-hide below is the second attempt */
            }
          };
          // Second attempt: clear the inline `display: none` Elation set on the
          // row. Only ever RE-shows something Elation hid; never touches a node
          // that is already visible.
          //
          // BOUNDED at the section's own checklist form (`span#pe-detail-checklist-form`,
          // the verified live container) — the widest thing the "Show all
          // checklist fields" trigger itself toggles. Un-hiding arbitrary
          // ancestors would let one write-back tear open unrelated collapsed
          // chrome in the clinician's editor; anything hidden ABOVE the form is
          // page state we do not own, so the write is refused instead.
          //
          // The bound only ever HELD when the row is a descendant of the
          // resolved form. When it wasn't, the walk never reached `stop`, ran on
          // to `document.body` and cleared `display: none` off every ancestor on
          // the way — tearing open unrelated collapsed chrome in the clinician's
          // editor, the exact thing this comment promises it never does. So
          // containment is now REQUIRED, and a row outside the form is refused
          // (its finding goes to the visible list entry) instead of revealed.
          //
          // Returns the inline styles it changed so a reveal that did not end in
          // a write can be put back — page state we opened for nothing.
          const revealAncestors = (
            el: Element,
          ): { node: HTMLElement; display: string; visibility: string }[] => {
            const undo: { node: HTMLElement; display: string; visibility: string }[] = [];
            try {
              if (!checklistForm || !checklistForm.contains || !checklistForm.contains(el)) {
                return undo;
              }
              const stop = checklistForm.parentElement;
              let node: Element | null = el;
              while (node && node !== stop && node !== document.body) {
                const style = (node as HTMLElement).style;
                const st = window.getComputedStyle
                  ? window.getComputedStyle(node)
                  : null;
                if (st && style) {
                  // Only ever CLEAR an inline `display: none` Elation set. When
                  // the hidden state came from a CLASS there is no inline value
                  // to clear, and forcing `display: block` guessed a display
                  // type — on `span#pe-detail-checklist-form`, an inline
                  // container, that changed the editor's layout. Leave it; the
                  // caller re-measures and refuses the field instead.
                  const changedDisplay = st.display === "none" && style.display === "none";
                  const changedVisibility =
                    st.visibility === "hidden" && style.visibility === "hidden";
                  if (changedDisplay || changedVisibility) {
                    undo.push({
                      node: node as HTMLElement,
                      display: style.display,
                      visibility: style.visibility,
                    });
                    if (changedDisplay) style.display = "";
                    if (changedVisibility) style.visibility = "";
                  }
                }
                node = node.parentElement;
              }
            } catch {
              /* nothing more we can do — the caller refuses the write */
            }
            return undo;
          };
          const restore = (
            undo: { node: HTMLElement; display: string; visibility: string }[],
          ): void => {
            try {
              for (let i = undo.length - 1; i >= 0; i -= 1) {
                undo[i].node.style.display = undo[i].display;
                undo[i].node.style.visibility = undo[i].visibility;
              }
            } catch {
              /* best effort */
            }
          };

          const plan: { system: string; label: string; text: string }[] = [];
          const leftovers: string[] = [];
          const bySystem: Record<string, { system: string; label: string; text: string }> = {};
          const resolveSystem = (label: string): string | null => {
            const norm = String(label || "")
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, " ")
              .trim();
            if (!norm) return null;
            const squashed = norm.replace(/\s+/g, "");
            for (let i = 0; i < checklistSystems.length; i += 1) {
              if (checklistSystems[i].toLowerCase() === squashed) return checklistSystems[i];
            }
            for (let i = 0; i < systemAliases.length; i += 1) {
              const entry = systemAliases[i];
              const cut = entry.indexOf(">");
              if (cut < 0 || entry.slice(0, cut) !== norm) continue;
              const cands = entry.slice(cut + 1).split(",");
              for (let j = 0; j < cands.length; j += 1) {
                if (checklistSystems.indexOf(cands[j]) !== -1) return cands[j];
              }
            }
            return null;
          };
          const addToSystem = (system: string, label: string, finding: string): void => {
            if (bySystem[system]) {
              bySystem[system].text = bySystem[system].text + "\n" + finding;
            } else {
              bySystem[system] = { system, label, text: finding };
            }
          };
          // A bare `Respiratory:` header attributes the lines BENEATH it.
          // Mirrors planChecklistWrite, INCLUDING what ends its scope: a blank
          // line, the next labelled line, or a bare heading naming no system.
          // The blank line is the clinically load-bearing one — without it
          // `All other systems reviewed and negative.`, a whole-exam negation a
          // paragraph below a `Respiratory:` header, was asserted as a
          // respiratory ROS finding, and counselling prose below a `Respiratory:`
          // exam header was asserted as a respiratory exam finding.
          // ── The provenance channel, decoded INLINE ──────────────────────
          // executeScript serializes this function, so `decodeSectionLines`
          // cannot be imported — this is a hand copy of it and MUST be exactly
          // as strict. Anything this accepted that the library rejects would be
          // a shape the host writes into a chart and the library refuses, so it
          // accepts nothing extra and FAILS CLOSED: malformed, truncated,
          // wrong-version or half-discriminant input leaves `decoded` null and
          // the legacy `text` loop runs unchanged. A bad entry aborts the WHOLE
          // payload (never a partial plan, never a half write).
          type DecodedLine = {
            text: string;
            systemLabel: string | null;
            finding: string | null;
          };
          let decoded: DecodedLine[] | null = null;
          // THE PROVENANCE CHANNEL MUST COVER THE BODY IT REPLACES. Once the
          // payload decodes, the `text` argument is never read again: the plan
          // comes from each line's `finding` and the visible list entry from
          // each line's `text`. So a payload that is VALID but SHORT silently
          // discards whatever `text` held and still reports success — a
          // one-entry payload against a three-line body wrote one line and threw
          // the other two away. The library's documented relation between the
          // two channels is exact — the text channel is the same lines in the
          // same order, each one bulleted — so it is checkable here, on the far
          // side of a package boundary this file does not compile: same count,
          // same lines, in order. A mismatch means the two channels disagree
          // about the physician's words, and the one this file can read whole is
          // `text`, so it falls back to it rather than write a subset.
          //
          // Deliberately tolerant of exactly two things and nothing else: the
          // library's `"- "` bullet on the text side (never stripped from what
          // gets WRITTEN — only from what gets COMPARED), and per-line
          // surrounding whitespace, which the host trims on the wire anyway.
          // Every non-whitespace character still has to match, in order.
          //
          // …AND THE CHANNEL THIS CHECKS MUST BE THE CHANNEL THAT IS WRITTEN.
          // Covering the body proved something about `text`, and `text` is the
          // one channel the decoded branch never reads again: the field VALUE is
          // `finding` and the field it lands in is chosen by `systemLabel`. So a
          // payload whose `text` matched the body line for line was accepted and
          // then wrote a TRUNCATED `finding` (`No wheezes` for
          // `No wheezes, no rales, no rhonchi`) or filed a respiratory finding
          // under `Cardiovascular` — wrong content, or right content attributed
          // to the wrong system, in a note a physician signs, reporting `ok`
          // both times. For a PAIRED line the library's relation between the
          // three channels is exact and documented (`text` is
          // `label + ": " + finding`), so it is checkable here, on the far side
          // of a package boundary this file does not compile, with the same
          // tolerance and the same fail-back: disagreement means the channels
          // disagree about the physician's words, and the one this file can read
          // whole is `text`. Unpaired lines (both discriminants null) have no
          // such relation to check and are unaffected.
          const compareLine = (s: string): string =>
            String(s == null ? "" : s)
              .replace(/^\s*-\s/, "")
              .replace(/\s+/g, " ")
              .trim();
          const channelsAgree = (line: DecodedLine): boolean => {
            if (line.systemLabel === null || line.finding === null) return true;
            return (
              compareLine(line.text) ===
              compareLine(line.systemLabel + ": " + line.finding)
            );
          };
          const coversBody = (read: DecodedLine[]): boolean => {
            for (let d = 0; d < read.length; d += 1) {
              if (!channelsAgree(read[d])) return false;
            }
            const body: string[] = [];
            for (const raw of String(text == null ? "" : text).split(/\r?\n/)) {
              if (raw.trim()) body.push(raw.trim());
            }
            const have: string[] = [];
            for (let d = 0; d < read.length; d += 1) {
              const t = read[d].text.trim();
              if (t) have.push(t);
            }
            if (body.length !== have.length) return false;
            for (let d = 0; d < have.length; d += 1) {
              if (have[d] === body[d]) continue;
              if (have[d] === body[d].replace(/^-\s/, "")) continue;
              return false;
            }
            return true;
          };
          if (linesJson) {
            try {
              const body: unknown = JSON.parse(linesJson);
              // `typeof body === "object" && body !== null` — not a truthy
              // check: JSON.parse("1") and JSON.parse('"a"') are truthy
              // primitives the library rejects before `v` is ever read.
              // `Array.isArray`, not a `.length` sniff: an array-LIKE object
              // ({"length":1,"0":{…}}) is rejected by the library too.
              if (typeof body === "object" && body !== null) {
                const envelope = body as { v?: unknown; lines?: unknown };
                if (envelope.v === 1 && Array.isArray(envelope.lines)) {
                  const raw = envelope.lines as unknown[];
                  let ok = true;
                  const read: DecodedLine[] = [];
                  for (let d = 0; d < raw.length; d += 1) {
                    const entry = raw[d] as {
                      text?: unknown;
                      systemLabel?: unknown;
                      finding?: unknown;
                    } | null;
                    // `typeof entry !== "object" || entry === null`, not `!entry`:
                    // a string or number entry is truthy and the library rejects
                    // it as a non-object rather than falling through.
                    if (typeof entry !== "object" || entry === null) {
                      ok = false;
                      break;
                    }
                    if (typeof entry.text !== "string") {
                      ok = false;
                      break;
                    }
                    if (entry.systemLabel === null && entry.finding === null) {
                      read.push({ text: entry.text, systemLabel: null, finding: null });
                    } else if (
                      typeof entry.systemLabel === "string" &&
                      typeof entry.finding === "string"
                    ) {
                      read.push({
                        text: entry.text,
                        systemLabel: entry.systemLabel,
                        finding: entry.finding,
                      });
                    } else {
                      // Half-populated discriminant — the library's `return null`.
                      ok = false;
                      break;
                    }
                  }
                  // AN EMPTY `lines` ARRAY IS NOT A PLAN, IT IS NO PROVENANCE.
                  // `{"v":1,"lines":[]}` is well-formed, so it used to reach the
                  // decoded branch with zero lines: nothing planned, nothing
                  // written, the whole `text` body thrown away, and a refusal
                  // naming a mechanism that never ran. It is the one
                  // accepted-but-useless payload, and the contract for a payload
                  // that carries no usable provenance is the same as for a
                  // malformed one — fall back to `text`.
                  if (ok && read.length && coversBody(read)) decoded = read;
                }
              }
            } catch {
              decoded = null;
            }
          }

          let headerSystem: string | null = null;
          let headerLabel = "";
          if (decoded) {
            // THE PROSE BRANCH IS FIRST, above every rewrite — the ordering IS
            // the fix. Nothing here reads the physician's text to decide
            // anything: attribution comes from `systemLabel`, the field value is
            // `finding`, and an unattributed line's `text` is passed through
            // byte-for-byte to the section's visible list entry. No glyph strip,
            // no numbering strip, no bare-heading skip, no colon split, no
            // re-trim — every one of those has mutated a physician's words.
            for (let i = 0; i < decoded.length; i += 1) {
              const line = decoded[i];
              if (line.systemLabel === null || line.finding === null) {
                leftovers.push(line.text);
                continue;
              }
              const named = resolveSystem(line.systemLabel);
              // An unmapped system is not data loss: the line still reaches the
              // chart, in the visible list entry, as the physician wrote it.
              if (!named) {
                leftovers.push(line.text);
                continue;
              }
              addToSystem(named, line.systemLabel, line.finding);
            }
          } else {
            const rawLines = text.split(/\r?\n/);
            for (let i = 0; i < rawLines.length; i += 1) {
              // NEVER MUTATE THE PHYSICIAN'S TEXT. Only a real list marker comes
              // off — one leading `-`/`*`/`•`/`·` or `1.`/`1)` FOLLOWED BY A
              // SPACE, which is formatting the renderer added. The old
              // class-based strip (`/^[\s>*•·-]+/`) ate clinical glyphs:
              // `>2 pillow orthopnea` became `2 pillow orthopnea`, `>=2+ edema`
              // became `=2+ edema`, `-5 lbs` became `5 lbs` — a weight LOSS
              // charted as a gain — and `1.5 cm scar` became `5 cm scar`, an
              // order-of-magnitude error in a signed note. Mirrors
              // planChecklistWrite.
              const line = rawLines[i]
                .replace(/^\s*(?:[-*•·]|\d+[.)])\s+/, "")
                .trim();
              if (!line) {
                headerSystem = null;
                headerLabel = "";
                continue;
              }
              // A bare `Something:` line with nothing after the colon is a HEADING
              // ("Review of Systems:") ONLY WHEN IT NAMES NO SYSTEM. Kept as a
              // leftover a true heading became a visible list entry INSIDE the
              // section it names — junk in a note a physician signs. But
              // `Respiratory:` on its own line is a SYSTEM HEADER, and dropping it
              // deleted a line of the draft and the attribution of every line
              // under it. Mirrors planChecklistWrite.
              const bare = line.match(/^([^:]{1,60}):$/);
              if (bare) {
                const named = resolveSystem(bare[1]);
                headerSystem = named;
                headerLabel = named ? bare[1].trim() : "";
                continue;
              }
              const matched = line.match(/^([^:]{1,60}):\s*(\S[\s\S]*)$/);
              const system = matched ? resolveSystem(matched[1]) : null;
              if (!matched || !system) {
                // Under a system header the line belongs to that system;
                // otherwise it is prose or an unmapped system and goes to the
                // visible list entry, never glued onto the last labelled system.
                if (headerSystem) addToSystem(headerSystem, headerLabel, line);
                else leftovers.push(line);
                continue;
              }
              headerSystem = null;
              headerLabel = "";
              addToSystem(system, matched[1].trim(), matched[2].trim());
            }
          }
          for (let i = 0; i < checklistSystems.length; i += 1) {
            const held = bySystem[checklistSystems[i]];
            if (held) plan.push(held);
          }

          const filled: string[] = [];
          const appended: string[] = [];
          const unchanged: string[] = [];
          const revealed: string[] = [];
          // Labels only — never finding text. `detail` is console-logged.
          const notPlaced: string[] = [];
          // Every system reported written, with the lines this call put in it, so
          // the final sweep can read them all back once more.
          const wroteLines: { system: string; label: string; lines: string[] }[] = [];
          // Every system reported as ALREADY holding the finding, with the lines
          // that claim names — the sweep reads these back too, so nothing is
          // reported as documented on the strength of a stale read.
          // `requireVisible` is false for the one case where a hidden row is the
          // CORRECT end state: the finding was already documented, so the reveal
          // was put back and the row is deliberately left exactly as the
          // clinician had it (F9). Everywhere else a claim about the chart is
          // also a claim the clinician can see it.
          const unchangedLines: {
            system: string;
            label: string;
            lines: string[];
            requireVisible: boolean;
          }[] = [];

          // REVEAL BEFORE ANY WRITE, NOT DURING. `clickShowAllChecklistFields`
          // runs ELATION'S OWN handler and may re-render the checklist. Fired
          // from inside the write loop, that re-render detached the handles of
          // the systems still to come (reported `filled CVS` into a node no
          // longer in the document) AND could wipe a field already written and
          // already verified. So: measure the planned rows first, click at most
          // once, then re-index — the whole write then runs against nodes that
          // exist after the re-render. 14 of 16 rows are hidden live, so this is
          // the normal path, not an edge case.
          const hiddenBefore: string[] = [];
          for (let i = 0; i < plan.length; i += 1) {
            const cand = fields[plan[i].system.toLowerCase()];
            if (!cand) continue;
            const candRow = (cand.closest ? cand.closest(".checklist-item") : null) || cand;
            if (isHidden(candRow)) hiddenBefore.push(plan[i].system);
          }
          if (hiddenBefore.length) {
            clickShowAllChecklistFields();
            indexChecklistFields();
            if (formsFound > 1 || foreignNoteId || duplicateSuffix || lockedSuffix) {
              return {
                ok: false,
                engine,
                section: sectionKey,
                // NAME THE REAL CAUSE. Folding `foreignNoteId` into
                // `SECTION_UNRESOLVED` told the clinician "the form or a field is
                // now ambiguous" on the one failure mode that means WRONG NOTE —
                // the note id changed between the guard and the write.
                errorCode: lockedSuffix
                  ? "SIGNED_NOTE_UNSUPPORTED"
                  : foreignNoteId
                    ? "OPEN_NOTE_MISMATCH"
                    : "SECTION_UNRESOLVED",
                detail:
                  "the checklist re-rendered after 'show all checklist fields' into a state that cannot be written safely (" +
                  (lockedSuffix
                    ? "field '" + normKey + "-" + lockedSuffix + "' is now disabled/readOnly"
                    : foreignNoteId
                      ? "the checklist now belongs to note " +
                        foreignNoteId +
                        ", the draft was written for " +
                        String(expectedNoteId)
                      : "the form or a field is now ambiguous") +
                  "); nothing was written",
              };
            }
            // A row the trigger un-hid is still a row this write revealed, and
            // the clinician's detail says so.
            for (let i = 0; i < hiddenBefore.length; i += 1) {
              const shown = fields[hiddenBefore[i].toLowerCase()];
              if (!shown) continue;
              const shownRow =
                (shown.closest ? shown.closest(".checklist-item") : null) || shown;
              if (!isHidden(shownRow)) revealed.push(hiddenBefore[i]);
            }
          }

          // THE INVARIANT: A REPORTED WRITE HAS BEEN READ BACK.
          //
          // Four separate defects on this feature all reported a write that
          // never reached the chart — a finding discarded by a substring test,
          // a signed note, the wrong form, and a detached node. Rather than
          // patch a fifth mechanism when someone finds it, nothing is counted as
          // `filled`/`appended` until the value has been read out of the field
          // that is IN THE LIVE DOCUMENT right now. The read re-resolves the
          // form and the field from scratch, so a handle that went stale, a
          // field that turned out to be locked, a form that turned out to be
          // another note's, and a write that simply did not take all fail the
          // same way — loudly.
          //
          // TOLERANT OF THE HOST'S OWN NORMALISATION, NOT OF A DIFFERENT VALUE.
          // Elation's `input`/autosave handlers may hand the value back with the
          // internal whitespace collapsed, a non-breaking space swapped in, or a
          // list marker re-attached by the row's renderer. Those are the same
          // clinical text, and refusing them made the feature cry
          // `WRITE_UNVERIFIED` while the finding was sitting in the field —
          // which teaches clinicians to hand-type findings and route around
          // write-back. So the comparison stays a WHOLE-LINE EQUALITY, tried in
          // TWO LANES and nothing else is stripped: the live line either equals
          // ours VERBATIM, or equals ours with ONE LEADING LIST MARKER ADDED. It
          // is deliberately NOT a substring/`indexOf` test — that is the exact
          // bug that made "No rales" already-present in "no wheezes, no rales,
          // no rhonchi" — so an added suffix, a re-wrap, or any other text still
          // fails. (`\s` covers the non-breaking space, so a nbsp the host
          // swapped in collapses to a plain space in both lanes.)
          //
          // WHY TWO LANES AND NOT ONE STRIP ON BOTH SIDES. Stripping the marker
          // from our side too makes the comparison symmetric, which is tempting
          // because it fixes the refusal below — but it stops verifying the
          // leading character in EITHER direction, and the observed host
          // behaviour is then marker SUBSTITUTION, not marker addition: a
          // finding `- 5 lbs since last visit` (a weight LOSS) against a renderer
          // that rewrites a leading `"- "` to `"• "` verifies as a success while
          // the chart holds `• 5 lbs since last visit` — the sign silently gone,
          // exactly the `-5 lbs` class this file's comments were written for.
          // Two lanes keep the addition tolerance the strip existed for and give
          // up no verification strength — the accept set is the pre-PR one plus
          // exact equality, a strict subset of the both-sides-stripped one: a
          // marker CHANGED or DROPPED from a line WE wrote still fails. The
          // converse direction is NOT closed and is not claimed to be: a live
          // line that already carries a marker (`- no rales`) still matches an
          // unmarked finding (`no rales`) through lane 2, so a host that dedupes
          // on the marker-stripped form can report `appended` without the line
          // count growing. That tolerance is pre-existing, and narrowing it would
          // cost the marker-addition case this lane exists for.
          /**
           * A FIELD'S CURRENT TEXT — AND *ABSENT* MEANS `null`/`undefined`, NOT
           * "FALSY".
           *
           * Every read of a checklist field's text goes through here. The reads
           * this replaces were `String(el.value || "")`, which is a
           * falsy-coercing test standing in for a null check: a `value` of the
           * number `0`, `NaN` or `false` reads back as `""` — i.e. as an EMPTY
           * FIELD — and an empty field is the one input that turns this file's
           * "APPEND, NEVER REPLACE" branch into a REPLACE, and turns the
           * rollback below into a write of `""` over whatever the clinician had.
           *
           * SCOPE, STATED HONESTLY. On a spec-conformant `HTMLTextAreaElement`
           * `.value` is a DOMString and is never a number, and the *string*
           * `"0"` is truthy in JS — so this is NOT a live data-loss path, and
           * the string-`"0"` field the tests pin behaved correctly before this
           * helper existed. What it removes is the coercion itself: this code
           * runs in the page's MAIN world, where `value` is whatever the host's
           * accessor hands back, and the file's own doctrine is that the host
           * may transform values on the way in and out. `mergeChecklistFinding`
           * — the pure helper this branch was inlined from — has always used
           * `String(existing ?? "")`; the inlined copy drifted to `||`. This
           * puts them back in agreement.
           *
           * `unknown` rather than `string` so a host-supplied non-string reaches
           * the null check instead of being pre-narrowed away by the type.
           */
          const readFieldValue = (el: { value?: unknown }): string =>
            el.value == null ? "" : String(el.value);
          const collapseForReadBack = (s: string): string =>
            String(s == null ? "" : s)
              .replace(/\s+/g, " ")
              .trim()
              .toLowerCase();
          const normaliseForReadBack = (s: string): string =>
            collapseForReadBack(
              String(s == null ? "" : s).replace(/^\s*(?:[-*•·]|\d+[.)])\s+/, ""),
            );
          const verifyWritten = (system: string, expectLines: string[]): boolean => {
            try {
              indexChecklistFields();
              if (formsFound > 1 || foreignNoteId || duplicateSuffix) return false;
              const live = fields[system.toLowerCase()];
              if (!live || !live.isConnected) return false;
              if (live.disabled || live.readOnly) return false;
              // Lane 1, the strict read: the live line as it stands.
              const haveStrict: string[] = [];
              // Lane 2, the forgiving read: the live line with one leading list
              // marker taken off, for the renderer that re-attaches one.
              const have: string[] = [];
              // ONLY `null`/`undefined` MEAN "NO VALUE" — see `before` below.
              for (const had of readFieldValue(live).split(/\r?\n/)) {
                const strict = collapseForReadBack(had);
                if (strict) haveStrict.push(strict);
                const t = normaliseForReadBack(had);
                if (t) have.push(t);
              }
              for (let k = 0; k < expectLines.length; k += 1) {
                // OUR SIDE IS NEVER STRIPPED. `finding` is the physician's line
                // verbatim and can legitimately BEGIN with a marker
                // (`* denotes patient-reported`, `1. first of the two lesions`).
                // Comparing that against the marker-stripped live value ONLY —
                // which is what this did — meant such a line could never match
                // itself: the value was assigned, the read-back failed, and the
                // call reported `WRITE_UNVERIFIED` ("nothing was reported as
                // written") with the finding sitting in the field. The fix is the
                // strict lane FIRST, so a marker-leading finding verifies against
                // itself, with the marker-added lane kept as the fallback.
                const want = collapseForReadBack(expectLines[k] || "");
                if (!want) continue;
                // The live line is ours, character for character.
                if (haveStrict.indexOf(want) !== -1) continue;
                // …or ours with a marker the host ADDED — the one observed
                // normalisation this tolerates.
                if (have.indexOf(want) !== -1) continue;
                // Anything else — a marker CHANGED, a marker DROPPED, a different
                // line — is a different value, and a different value is a failure.
                return false;
              }
              return true;
            } catch {
              return false;
            }
          };
          // …AND THE OTHER HALF OF THE INVARIANT: PRESENT *AND* VISIBLE.
          //
          // `isHidden` was measured once per row, BEFORE its write, and never
          // again. So the invariant covered the field's VALUE and nothing
          // covered the row's VISIBILITY, which is the half the code's own
          // policy cares about most: "REVEAL OR DON'T WRITE. An
          // invisible-but-persisted finding is the worst outcome available
          // here." A row Elation re-collapsed after our reveal therefore
          // verified, reported `filled`, and left clinical text in a
          // `display: none` row inside a note the clinician can sign without
          // ever seeing it — the sixth mechanism on this feature to report a
          // write that had not effectively happened.
          //
          // The reveal is an INLINE `style.display = ""` on a live node, so any
          // re-render of that row from Elation's own markup restores the inline
          // `display: none` while the value survives (our `input`/`change`
          // dispatch put it into Elation's model). Two shapes were reproduced:
          // the written field's own `input` handler re-hiding its own row, and a
          // LATER field's handler re-hiding an EARLIER row that had already
          // verified — the same causal shape as the defect the `wroteLines`
          // sweep itself was added for.
          //
          // CLOSES THE CLASS, NOT THE TWO REPRODUCTIONS. This is checked in the
          // same final sweeps as the value, i.e. at the LAST moment the write
          // can observe anything, after every field has been touched and every
          // handler those touches fired has run. Any post-write DOM change that
          // hides the row — its own handler, another field's handler, Elation's
          // autosave re-render, a stylesheet the re-render attaches, a collapsed
          // ancestor — is hidden by then and is caught, because the measurement
          // is of the row's computed visibility rather than of the mechanism
          // that hid it. It re-resolves the field from the live document for the
          // same reason the value read does: a handle can be detached.
          const rowShown = (system: string): boolean => {
            try {
              indexChecklistFields();
              const live = fields[system.toLowerCase()];
              if (!live || !live.isConnected) return false;
              const row =
                (live.closest ? live.closest(".checklist-item") : null) || live;
              return !isHidden(row);
            } catch {
              /* cannot measure — the same answer as `isHidden`: assume hidden */
              return false;
            }
          };

          // …AND A THIRD HALF, ABOUT THE SENTENCE RATHER THAN THE WRITE: NO
          // CLAUSE OF `detail` MAY DESCRIBE A FIELD THAT WAS NOT JUST READ.
          //
          // The value and visibility halves above stopped the code REPORTING a
          // write it had not read. They did not stop it DESCRIBING a field it had
          // not read, and that is where the next two false statements came from —
          // both introduced by remediating a *wording* finding:
          //   · "X was written and verified, and is still in the note" — a
          //     present-tense claim about a field this call last touched several
          //     mechanisms ago. This return aborts the loop, so the `wroteLines`
          //     sweep never runs, and the sweep exists PRECISELY because a later
          //     field's `input` handler can clear an earlier one. Reproduced:
          //     `pe-CVS`'s handler emptied `pe-Resp`, and the clinician was told
          //     `Resp` was still in the note while it held "".
          //   · "That field was put back to the value it held before this write"
          //     — read back from the node we assigned to, with no `isConnected`
          //     check, so on a node the host had already replaced it read back its
          //     own assignment and self-confirmed while the LIVE field held our
          //     marker-substituted text.
          // So the clauses are built from these two readers and nothing else. A
          // sentence about a field is unconstructable without the read that earns
          // it, which closes the class instead of correcting its third instance.
          type FieldState = "holds" | "hidden" | "lost";
          const readFieldState = (system: string, lines: string[]): FieldState => {
            if (!verifyWritten(system, lines)) return "lost";
            return rowShown(system) ? "holds" : "hidden";
          };
          /**
           * What the fields this write already reported are holding RIGHT NOW,
           * read one last time on the way out of a refusal that skips the sweeps.
           */
          const residueClause = (): string => {
            const holds: string[] = [];
            const hidden: string[] = [];
            const lost: string[] = [];
            for (let i = 0; i < wroteLines.length; i += 1) {
              const w = wroteLines[i];
              const state = readFieldState(w.system, w.lines);
              if (state === "holds") holds.push(w.system);
              else if (state === "hidden") hidden.push(w.system);
              else lost.push(w.system);
            }
            return (
              (holds.length
                ? "Read back just now: " +
                  holds.join(", ") +
                  " still holds what this write put there. "
                : "") +
              (hidden.length
                ? "Read back just now: " +
                  hidden.join(", ") +
                  " still holds what this write put there but its row is hidden, so it would not be visible in the note — check the note. "
                : "") +
              (lost.length
                ? "Read back just now: " +
                  lost.join(", ") +
                  " no longer holds what this write put there — check the note. "
                : "")
            );
          };

          for (let i = 0; i < plan.length; i += 1) {
            const entry = plan[i];
            const slot = entry.system.toLowerCase();
            // A handle indexed earlier in this call may have been detached by a
            // re-render since. Re-resolve rather than write into a node that is
            // no longer in the document.
            if (fields[slot] && !fields[slot].isConnected) indexChecklistFields();
            let el = fields[slot];
            if (!el) {
              notPlaced.push(entry.label);
              leftovers.push(entry.label + ": " + entry.text);
              continue;
            }
            let row: Element =
              (el.closest ? el.closest(".checklist-item") : null) || el;
            let undo: { node: HTMLElement; display: string; visibility: string }[] = [];
            if (isHidden(row)) {
              undo = revealAncestors(row);
              if (isHidden(row)) {
                // REVEAL OR DON'T WRITE. An invisible-but-persisted finding is
                // the worst outcome available here, so the text goes into the
                // section's list entry instead, where it is visible. Put back
                // whatever the failed reveal opened: we changed the clinician's
                // page and wrote nothing there.
                restore(undo);
                notPlaced.push(entry.label);
                leftovers.push(entry.label + ": " + entry.text);
                continue;
              }
              revealed.push(entry.system);
            }
            // `readFieldValue`, not `el.value || ""`: only null/undefined mean
            // "the field is empty", and "the field is empty" is what decides
            // REPLACE vs APPEND four lines down and what the rollback restores.
            const before = readFieldValue(el);
            // APPEND, NEVER REPLACE (hazard 2) — mirrors mergeChecklistFinding.
            // The already-present test is LINE-WISE. `indexOf` threw findings
            // away: the pre-filled normals are long negation sentences, so
            // "No rales" was "already present" in "No wheezes, no rales, no
            // rhonchi" and was silently discarded while the write claimed
            // success. Per line, so a two-line finding half of which is already
            // charted still gets its other half written.
            const add = entry.text.trim();
            let next: string | null = null;
            // The lines this write is responsible for putting in the field —
            // exactly what the read-back below has to find there.
            const freshLines: string[] = [];
            // Every non-blank line of the finding, whether it is fresh or was
            // already there. When NOTHING is fresh these are the lines the
            // `already present in X` claim rests on, so they are what the sweep
            // has to read back.
            const allLines: string[] = [];
            if (add) {
              // ONE dedupe for both branches, so a line repeated WITHIN this
              // batch is written once. `Respiratory: Coarse crackles` twice in
              // one draft used to land as two identical lines in `pe-Resp` when
              // the field started empty — the dedupe only ran against the
              // field's existing text.
              const have: string[] = [];
              for (const had of before.split(/\r?\n/)) {
                const t = had.trim().toLowerCase();
                if (t) have.push(t);
              }
              for (const line of add.split(/\r?\n/)) {
                const t = line.trim();
                if (!t) continue;
                allLines.push(t);
                if (have.indexOf(t.toLowerCase()) !== -1) continue;
                have.push(t.toLowerCase());
                freshLines.push(t);
              }
              if (freshLines.length) {
                next = before.trim()
                  ? before.replace(/\s+$/, "") + "\n" + freshLines.join("\n")
                  : freshLines.join("\n");
              }
            }
            if (next == null) {
              // Genuinely already documented, line for line — an idempotent
              // no-op, not a discarded finding. Revealing the row for it was
              // pointless page state, so put it back.
              restore(undo);
              if (undo.length) {
                const at = revealed.indexOf(entry.system);
                if (at !== -1) revealed.splice(at, 1);
              }
              unchanged.push(entry.system);
              // `already present in X` IS A CLAIM ABOUT THE CHART, so it obeys
              // the same invariant as `filled`: it was decided from a read taken
              // BEFORE the rest of the write ran, and a later field's `input`
              // handler can clear this one. That produced the fifth false
              // success — `already present in General` reported while
              // `pe-General` was empty, telling the clinician the finding was
              // documented and suppressing the retry. Re-read in the final
              // sweep with the other claims.
              unchangedLines.push({
                system: entry.system,
                label: entry.label,
                lines: allLines,
                requireVisible: undo.length === 0,
              });
              continue;
            }
            el.value = next;
            // Let Elation's own handlers see the edit (autosave / dirty state).
            try {
              if (typeof Event === "function") {
                el.dispatchEvent(new Event("input", { bubbles: true }));
                el.dispatchEvent(new Event("change", { bubbles: true }));
              }
            } catch {
              /* the value is set either way; Elation reads the field on save */
            }
            // READ IT BACK, OR SAY SO. Never report a write on the strength of
            // having assigned to a property.
            if (!verifyWritten(entry.system, freshLines)) {
              // AND PUT IT BACK — THEN READ THE PUT-BACK BACK TOO. The refusal
              // happens AFTER `el.value = next`, so the report described this
              // function's return value while the finding was in the clinician's
              // field, under an error code that also aborts the loop. So the
              // invariant now undoes its own mutation before it refuses.
              //
              // BUT THE UNDO IS ITSELF A WRITE, AND THIS FILE'S DOCTRINE IS THAT
              // A WRITE IS NOT A FACT UNTIL IT HAS BEEN READ BACK. Worse, the
              // undo runs ONLY after a read-back failed, and the likeliest cause
              // of that failure is a host that transforms values on assignment —
              // so it executes disproportionately in exactly the conditions where
              // it cannot be trusted. Asserted and unread, it corrupted the
              // clinician's PRE-EXISTING sentence (a host appending on every
              // assignment turned `Pre-existing: normal respiratory exam.` into
              // `Pre-existing: normal respiratory exam. [host mangled]`) under a
              // report saying nothing was written. So the restore is read back
              // once — no retry loop — and `detail` says which of the two
              // outcomes actually happened.
              //
              // NOT A MUTATION OF THE PHYSICIAN'S TEXT WHEN IT WORKS: `before` is
              // this field's own value as read out of the live DOM immediately
              // above, so a successful restore returns the clinician's prior
              // state byte for byte. It is only ever the node THIS iteration
              // assigned to — never a re-resolved one, which could belong to a
              // re-rendered field this write never touched. The events go out for
              // the same reason the write's did: our `input` dispatch put `next`
              // into Elation's own model, and only another dispatch takes it back
              // out again.
              // FOUR OUTCOMES, EACH ONE READ. `restored` was a boolean whose
              // true branch claimed byte-for-byte restoration while forgiving
              // case changes and newline flattening — so it claimed more than it
              // knew. And it read the node this iteration assigned to WITHOUT
              // asking whether that node is still in the document, which on a
              // replaced field means reading back your own assignment: the undo
              // self-confirmed while the live field held our text. `isConnected`
              // is the same question `verifyWritten` and `rowShown` both ask
              // before they will say anything, and it is asked AFTER the dispatch
              // so a node our own undo detached is caught too.
              let restoreState: "exact" | "normalised" | "detached" | "no" = "no";
              try {
                el.value = before;
                try {
                  if (typeof Event === "function") {
                    el.dispatchEvent(new Event("input", { bubbles: true }));
                    el.dispatchEvent(new Event("change", { bubbles: true }));
                  }
                } catch {
                  /* the assignment stands or does not; the read below decides */
                }
                if (!el.isConnected) {
                  // The host swapped the field out from under the write. Our undo
                  // went into a node nobody reads, and the value the first
                  // dispatch put into Elation's model is what the replacement was
                  // populated from — which we may not write to, having never
                  // written to it.
                  restoreState = "detached";
                } else {
                  const back = readFieldValue(el);
                  restoreState =
                    back === before
                      ? "exact"
                      : collapseForReadBack(back) === collapseForReadBack(before)
                        ? // Same characters modulo the case, whitespace and line
                          // breaks the collapse forgives — which is NOT byte
                          // identity, so it does not get the sentence that claims
                          // byte identity.
                          "normalised"
                        : "no";
                }
              } catch {
                /* a setter that refused our write refuses the undo too */
                restoreState = "no";
              }
              // Same as the failed reveal and the idempotent no-op above: we
              // opened rows on the clinician's page for a write that did not
              // happen, so they close again.
              restore(undo);
              if (undo.length) {
                const at = revealed.indexOf(entry.system);
                if (at !== -1) revealed.splice(at, 1);
              }
              // SCOPED HONESTLY. This return aborts the loop, so any system that
              // already verified keeps its text in the note and never reaches the
              // two final sweeps. "Nothing was written" was therefore never true
              // of the WRITE, only of this field. The residue is stated before the
              // summary, in the present tense ONLY because `residueClause`
              // re-reads every one of those fields to build it, and the summary
              // claims only what this function can support: no result reported.
              return {
                ok: false,
                engine,
                section: sectionKey,
                errorCode: "WRITE_UNVERIFIED",
                detail:
                  "wrote " +
                  entry.label +
                  " but could not read it back out of the live '" +
                  normKey +
                  "-" +
                  entry.system +
                  "' field — the checklist may have re-rendered or the note may be locked. " +
                  (restoreState === "exact"
                    ? "That field was put back to the value it held before this write. "
                    : restoreState === "normalised"
                      ? "That field was put back, but the host normalised it, so it is not byte-identical to the value it held before this write — check it in the note. "
                      : restoreState === "detached"
                        ? "That field was replaced while this write ran, so it could not be put back — check it in the note. "
                        : "That field could NOT be put back to the value it held before this write — check it in the note. ") +
                  residueClause() +
                  "No result was reported for this write; check the note before trying again.",
              };
            }
            wroteLines.push({ system: entry.system, label: entry.label, lines: freshLines });
            if (before.trim()) appended.push(entry.system);
            else filled.push(entry.system);
          }

          // FINAL SWEEP. A re-render triggered by a LATER field's own `input`
          // handler can wipe an earlier field that already verified, so every
          // reported write is read back once more after the last one. Only then
          // does `filled`/`appended` go into the detail the clinician's panel
          // shows.
          for (let i = 0; i < wroteLines.length; i += 1) {
            const w = wroteLines[i];
            if (verifyWritten(w.system, w.lines)) {
              if (rowShown(w.system)) continue;
              return {
                ok: false,
                engine,
                section: sectionKey,
                errorCode: "WRITE_UNVERIFIED",
                detail:
                  "the '" +
                  normKey +
                  "-" +
                  w.system +
                  "' field holds what was written to it (" +
                  w.label +
                  ") but its row is hidden again — the finding would not be visible in the note; check the note before trying again",
              };
            }
            return {
              ok: false,
              engine,
              section: sectionKey,
              errorCode: "WRITE_UNVERIFIED",
              detail:
                "the '" +
                normKey +
                "-" +
                w.system +
                "' field no longer holds what was written to it (" +
                w.label +
                ") — the checklist changed under the write; check the note before trying again",
            };
          }
          // AND THE SAME SWEEP FOR THE `already present` CLAIMS. If we are about
          // to tell the clinician the finding is already documented, we must have
          // READ it there after everything else in this write landed.
          for (let i = 0; i < unchangedLines.length; i += 1) {
            const u = unchangedLines[i];
            if (verifyWritten(u.system, u.lines)) {
              if (!u.requireVisible || rowShown(u.system)) continue;
              return {
                ok: false,
                engine,
                section: sectionKey,
                errorCode: "WRITE_UNVERIFIED",
                detail:
                  "the '" +
                  normKey +
                  "-" +
                  u.system +
                  "' field holds the finding this write reported as already present (" +
                  u.label +
                  ") but its row is hidden again — the finding would not be visible in the note; check the note before trying again",
              };
            }
            return {
              ok: false,
              engine,
              section: sectionKey,
              errorCode: "WRITE_UNVERIFIED",
              detail:
                "the '" +
                normKey +
                "-" +
                u.system +
                "' field does not hold the finding this write reported as already present (" +
                u.label +
                ") — the checklist changed under the write; check the note before trying again",
            };
          }

          // THE LEFTOVER HALF IS ALSO IDEMPOTENT. It had no dedupe at all, so a
          // second press added a SECOND identical list entry — and that is the
          // normal case, not an edge case: `Allergic and immunologic` (ros) and
          // `Extremities` (pe) are deliberately unmapped, so every complete
          // draft produces at least one leftover. Compare against the entries the
          // section already holds: the model first (live-verified — a listeditor's
          // `getJsonData()` returns `[{ text }]` for entries added this session,
          // and null when there are none), then the rendered `<ol>`, which is
          // what survives a reload.
          const alreadyEntered: string[] = [];
          try {
            const sec = sectionsMap[sectionKey];
            const j = sec && typeof sec.getJsonData === "function" ? sec.getJsonData() : null;
            if (j && typeof j.length === "number") {
              for (let i = 0; i < j.length; i += 1) {
                const e = j[i];
                const t = e && typeof e.text === "string" ? e.text : "";
                for (const line of t.split(/\r?\n/)) {
                  const k = line.trim().toLowerCase();
                  if (k) alreadyEntered.push(k);
                }
              }
            }
          } catch {
            /* no model read — the <ol> below may still answer */
          }
          try {
            const ol = document.getElementById("visit-note-list-" + normKey);
            const items = ol ? ol.querySelectorAll("li") : null;
            for (let i = 0; items && i < items.length; i += 1) {
              const item = items[i] as HTMLElement;
              const t = String(item.innerText || item.textContent || "");
              for (const line of t.split(/\r?\n/)) {
                const k = line.trim().toLowerCase();
                if (k) alreadyEntered.push(k);
              }
            }
          } catch {
            /* no list rendered — nothing to dedupe against */
          }
          const freshLeftovers: string[] = [];
          let duplicateLeftovers = 0;
          for (let i = 0; i < leftovers.length; i += 1) {
            const k = leftovers[i].trim().toLowerCase();
            if (!k) continue;
            if (alreadyEntered.indexOf(k) !== -1) {
              duplicateLeftovers += 1;
              continue;
            }
            alreadyEntered.push(k);
            freshLeftovers.push(leftovers[i]);
          }

          let bulletFailed = false;
          if (freshLeftovers.length) {
            try {
              const cap = sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1);
              const N = m._model.addBullet(cap, { text: freshLeftovers.join("\n") });
              sectionsMap[sectionKey].addEntry(N, true, undefined);
            } catch {
              bulletFailed = true;
            }
          }

          const wrote = filled.length + appended.length;
          // DEGRADED: a system the draft named resolved to an Elation field this
          // page does not have (or has but could not show). The per-system keys
          // are PRACTICE CONFIGURATION, not registry data, so a practice whose
          // checklist is named differently from the 17/16 above puts the whole
          // body back into one list entry — the originally-reported defect,
          // restored — and without this flag that looked exactly like success.
          const degraded = notPlaced.length > 0;
          const parts: string[] = [];
          if (filled.length) parts.push("filled " + filled.join(", "));
          if (appended.length) {
            parts.push("appended below existing text in " + appended.join(", "));
          }
          if (unchanged.length) {
            parts.push("already present in " + unchanged.join(", "));
          }
          if (revealed.length) parts.push("revealed hidden " + revealed.join(", "));
          if (notPlaced.length) {
            parts.push(
              "no field for " + notPlaced.join(", ") + " — added as a list entry",
            );
          }
          if (freshLeftovers.length && !notPlaced.length) {
            parts.push(freshLeftovers.length + " line(s) added as a list entry");
          }
          if (duplicateLeftovers) {
            parts.push(duplicateLeftovers + " line(s) already in the list entry");
          }
          if (bulletFailed) parts.push("list entry FAILED");
          const detail =
            "per-system write" +
            (degraded ? " (DEGRADED — this practice's checklist field names may differ)" : "") +
            ": " +
            (parts.join("; ") || "nothing to write");

          // Only a write where NOTHING landed anywhere is a failure.
          if (
            wrote === 0 &&
            unchanged.length === 0 &&
            duplicateLeftovers === 0 &&
            (!freshLeftovers.length || bulletFailed)
          ) {
            return {
              ok: false,
              engine,
              section: sectionKey,
              // NAME WHAT ACTUALLY HAPPENED. Two different things end up here:
              // the list-entry write THREW (`ADDBULLET_THREW` is exactly right),
              // or there was nothing to place anywhere and it never ran — for
              // which `ADDBULLET_THREW` names a mechanism that did not execute
              // and shows the clinician a re-try message for a failure that
              // isn't one. `EMPTY_TEXT` renders as "There's nothing to write."
              errorCode: bulletFailed ? "ADDBULLET_THREW" : "EMPTY_TEXT",
              detail,
            };
          }
          return { ok: true, engine, section: sectionKey, degraded, detail };
        }
      }

      try {
        const cap = sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1);
        const N = m._model.addBullet(cap, { text });
        sectionsMap[sectionKey].addEntry(N, true, undefined);
      } catch (e) {
        return { ok: false, engine, section: sectionKey, errorCode: "ADDBULLET_THREW", detail: String(e) };
      }
      return { ok: true, engine, section: sectionKey };
    }

    const template = m.getCurrFormTemplate?.();
    const mapFn: ((r: string) => string) | undefined = template?.mapReferenceFn?.bind(template);
    const sections = m._inputSections;
    if (typeof mapFn !== "function" || !sections) {
      return { ok: false, engine, errorCode: "EL8_UNAVAILABLE", detail: "template.mapReferenceFn or _inputSections missing" };
    }

    // ── Section resolution (duplicate of resolveSectionRef) ───────────
    let chosenRef: string | null = null;
    for (const ref of preferredRefs) {
      const key = mapFn(ref);
      if (key && Object.prototype.hasOwnProperty.call(sections, key) && sections[key]) {
        chosenRef = ref;
        break;
      }
    }
    if (!chosenRef) {
      return {
        ok: false,
        engine,
        errorCode: "SECTION_UNRESOLVED",
        detail: `none of [${preferredRefs.join(", ")}] map to a real section; available: ${Object.keys(sections).join(", ")}`,
      };
    }

    // ── MANDATORY guard: confirm mapped key is a real section ─────────
    // (Refusing here prevents the orphaned-bullet corruption where addText
    // mutates _model FIRST, then throws on an undefined _inputSections[key].)
    // This re-check relies on `mapReferenceFn` being a pure/idempotent lookup
    // for the currently-open template (same ref -> same key every call); it
    // does NOT re-validate against a live DOM state, so `addText` internally
    // re-resolves the reference itself at write time.
    const mappedKey = mapFn(chosenRef);
    if (!Object.prototype.hasOwnProperty.call(sections, mappedKey) || !sections[mappedKey]) {
      return { ok: false, engine, section: chosenRef, errorCode: "SECTION_UNRESOLVED", detail: `mapped key '${mappedKey}' is not a live section — refusing (would corrupt note)` };
    }

    // ── The write ──────────────────────────────────────────────────────
    try {
      el8.appchart.mgr.notes.addText(text, chosenRef);
    } catch (e) {
      // On the legacy engine, reaching here means the mandatory guard above
      // already confirmed a live section — so a throw from addText is NOT a
      // benign, safely-retryable failure. It implies the note may already be
      // partially mutated (the orphaned-bullet corruption mode this guard
      // exists to prevent). Treat as a hard error, surface it, and don't
      // auto-retry.
      return { ok: false, engine, section: chosenRef, errorCode: "ADDBULLET_THREW", detail: String(e) };
    }

    return { ok: true, engine, section: chosenRef };
  } catch (e) {
    return { ok: false, engine: "unknown", errorCode: "ADDBULLET_THREW", detail: String(e) };
  }
}

// ── Section read (MAIN world, injected) ──────────────────────────────────────

/**
 * Injected into the Elation tab's MAIN world via
 * `chrome.scripting.executeScript({ world: "MAIN", func: readNoteSectionsInPage })`
 * to read the ACTUAL sections of the currently-open note. Feeds the per-section
 * write-back UI (ACT-1242).
 *
 * MUST be fully self-contained — executeScript serializes it, so it cannot
 * reference anything outside its own body (no imports, no module-scope consts,
 * no calls to other functions in this module). Returns a plain object the
 * sidepanel normalizes with `normalizeSections`.
 *
 * Contract (reverse-engineered, ACT-1242):
 *  - legacy: `window.el8.appchart.visitNote.main`; if `isOpen()`, enumerate
 *    `_inputSections` — `key` is the exact map key, `label` from `sec.getName()`,
 *    `currentText` from `sec.getJsonData()` (app-object model; DOM fallback),
 *    `writable` from `sec.getType()` (textarea/listeditor = writable; multilist
 *    only for MULTILIST_WRITABLE_KEYS; textlist never).
 *  - vn2 (`visitNote2`) / nonvisit (`nonvisitNote`): recognized but no sections
 *    (AI-note write-back not supported yet) → `{ engine, sections: [] }`.
 *  - otherwise `{ ok: true, engine: "none", sections: [] }` — the read RAN and
 *    no editable note is open.
 *
 * Every path that could not read the chart at all returns `ok: false` instead,
 * with a `failure` reason. Both are load-bearing: `resolveSectionInventory` may
 * carry a previous inventory through `ok: false, failure: "unreadable"` (a
 * transport blip), and must never do so through `ok: true, engine: "none"` (a
 * signed or closed note) or `failure: "patient-mismatch"` (a different chart).
 */
export function readNoteSectionsInPage(expectedPatientId?: string): NoteSectionsResult {
  try {
    // ── Patient-match guard ───────────────────────────────────────────
    // The caller resolves a tab by "most relevant EHR tab", which prefers the
    // FOREGROUND one — so with patient A in tab 1 and patient B in tab 2, this
    // read can land in tab 2 while the snapshot it enriches came from tab 1.
    // B's sections (which feed the generation prompt) and B's note id would
    // then be merged onto A's open note: a cross-patient PHI mix, and an
    // identity check that validates B against B and passes.
    //
    // Same defence `stageInPage` already applies, for the same reason: only the
    // page knows which chart it actually is. Refuse rather than return data the
    // caller will attribute to the wrong patient.
    //
    // The REASON matters as much as the refusal, because this guard covers two
    // very different situations and the caller treats them differently:
    //
    //  - the page names a patient and it is a DIFFERENT one. This read knows it
    //    is looking at the wrong chart, so it says so — `"patient-mismatch"`,
    //    which forbids any carry-forward. That is the in-tab patient switch: the
    //    ~300ms `ELATION_LIVE_NOTE` push has already merged patient B's note
    //    onto a snapshot whose `patient` is still A (the patient poll is a
    //    separate, up-to-10s channel), so every read in that window fails here.
    //    Carrying through it published A's charted section text and A's note id
    //    on B's note, and sent them to the generation mutation.
    //  - the page names NO patient (Elation mid-navigation, `location.pathname`
    //    between charts). Identity is UNKNOWN, not wrong. This is one of the
    //    transient blips the carry-forward exists for, so it stays
    //    `"unreadable"`.
    const livePatientId = location.pathname.match(/\/patient\/(\d+)/)?.[1];
    if (expectedPatientId && livePatientId !== expectedPatientId) {
      return {
        ok: false,
        engine: "none",
        sections: [],
        failure: livePatientId ? "patient-mismatch" : "unreadable",
      };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const el8: any = (window as any).el8;
    const chart = el8?.appchart;
    if (!chart) return { ok: false, engine: "none", sections: [], failure: "unreadable" };

    // Read the live content of a section (ACT-1242 bug 1). Self-contained
    // (inline) — this fn is serialized and cannot call helpers.
    //
    // PREFER the app-object model read: `sec.getJsonData()` returns
    // `[{ text }, …]` for free-text sections (textarea/listeditor) and is
    // reliable even when the section's DOM field is empty. Live-verified: a
    // SOAP/Simple Follow-up with content came back "" from the DOM scrape but
    // getJsonData() had the text — the exact bug. Fall back to the DOM scrape
    // only when getJsonData is unavailable/empty.
    // A `listeditor` section (pe, ros) keeps its content NOWHERE the model can
    // see it. Live-verified 2026-08-06 on a SOAP note whose exam was fully
    // populated: `pe.getJsonData()` === null, `pe.getMultilistEntries()` ===
    // undefined, and `<ol id="visit-note-list-pe">` had ZERO <li> — while the
    // 16 `textarea[name="pe-<System>"]` checklist fields held every line of it
    // (pe-General "NAD, Alert, not well-appearing…", pe-Resp, pe-Pelvic, …).
    // So the model read above returns nothing for these sections and the
    // generic scrape below is what runs — and it gets two things wrong:
    //
    //  1. It ingests the section's LEAD ENTRY input, `textarea[name="pe"]`,
    //     whose live value is the template's PROMPT TEXT — literally
    //     "physical exam findings..." for pe and a 20-char equivalent for ros.
    //     That is a placeholder stored as a `.value`, not a `placeholder`
    //     attribute, so nothing about it looks like a placeholder to a scrape.
    //     It would be handed to the model as charted clinical content.
    //  2. It joins the per-system fields with no labels, so "Clear to
    //     auscultation." arrives with no indication it is the RESPIRATORY
    //     finding — the one thing that makes an exam line mean anything.
    //
    // Read those sections deliberately instead: committed list entries first,
    // then the checklist fields as `System: finding` pairs. Queried from the
    // document by NAME PREFIX rather than through `sec._container`, because the
    // checklist form is a sibling of the list container on the live page and
    // the names are already namespaced per section key. Self-contained (this fn
    // is serialized into the MAIN world and cannot call helpers).
    const readListEditorText = (key: string): { text: string; handled: boolean } => {
      const safeKey = String(key || "").replace(/[^A-Za-z0-9_]/g, "");
      if (!safeKey) return { text: "", handled: false };
      const lines: string[] = [];
      // Entries committed into the listeditor's own <ol> (this is where an
      // addBullet+addEntry write lands, so a round-trip read must see them).
      try {
        const ol = document.getElementById("visit-note-list-" + safeKey);
        if (ol) {
          const items = ol.querySelectorAll("li");
          for (let i = 0; i < items.length; i += 1) {
            const item = items[i] as HTMLElement;
            // `innerText` on the live page (respects the rendered line breaks
            // Elation inserts); `textContent` because jsdom does not implement
            // innerText, so a test-only read would silently see "".
            const t = String(item.innerText || item.textContent || "").trim();
            if (t) lines.push(t);
          }
        }
      } catch {
        /* no list rendered — checklist fields may still answer */
      }
      let sawChecklist = false;
      try {
        const fields = document.querySelectorAll(
          'textarea[name^="' + safeKey + '-"]',
        );
        sawChecklist = fields.length > 0;
        for (let i = 0; i < fields.length; i += 1) {
          const el = fields[i] as HTMLTextAreaElement;
          // Same rule as `readFieldValue` in `stageInPage`: absent is
          // null/undefined, not falsy. (`executeScript` serializes each of these
          // functions on its own, so they cannot share the helper.) The
          // emptiness decision itself is the `.trim()` below, which is already
          // correct for a field holding the string "0".
          const value = String(el.value == null ? "" : el.value).trim();
          if (!value) continue;
          // Prefer the rendered label ("Resp", "CVS"); fall back to the name
          // suffix, which is the same word on every template seen live.
          let label = "";
          try {
            const item = el.closest ? el.closest(".checklist-item") : null;
            const lab = item ? item.querySelector("label") : null;
            label = lab ? String(lab.textContent || "").trim() : "";
          } catch {
            /* fall back to the name suffix */
          }
          if (!label) label = String(el.name || "").slice(safeKey.length + 1);
          lines.push(label ? label + ": " + value : value);
        }
      } catch {
        /* querySelectorAll unavailable — fall through */
      }
      // `handled` claims the read, suppressing BOTH the model read and the
      // generic scrape. Claimed only when this section HAS a checklist — and then
      // always, even if every field is empty, because an empty exam must read as
      // empty rather than as "physical exam findings...".
      //
      // Deliberately NOT claimed on `lines.length > 0` alone. A section with an
      // `<ol>` but no checklist (e.g. `instr`, a multilist) is better served by
      // `getJsonData()`, which returns structured entries INCLUDING children,
      // where this reader would flatten them to `innerText`.
      return { text: lines.join("\n"), handled: sawChecklist };
    };

    const readSectionText = (sec: any, key: string): string => {
      // CHECKLIST-BACKED SECTIONS FIRST — the ordering is the whole point.
      //
      // `pe.getJsonData()` is NOT blind to these sections, which an earlier
      // version of this code assumed: it returns the section's `<ol>` ENTRIES,
      // and is null only when there are none. What it never sees is the
      // per-system checklist, where the actual exam lives. Live 2026-08-06, on
      // one chart, in this order: with an empty list and a fully charted exam it
      // returned `null`; after a single bullet was added it returned
      // `[{ text: "…" }]` — one entry, while the 16 charted systems stayed
      // invisible to it.
      //
      // So preferring the model read here would return that one bullet and
      // SILENTLY DROP THE ENTIRE EXAM — the same class of defect this function
      // exists to fix, reintroduced by ordering. Ask the checklist reader first;
      // it covers both halves (`<ol>` entries AND the per-system fields).
      try {
        const le = readListEditorText(key);
        if (le.handled) return le.text;
      } catch {
        /* fall through to the model read */
      }
      try {
        if (sec && typeof sec.getJsonData === "function") {
          const j = sec.getJsonData();
          if (Array.isArray(j)) {
            const t = j
              .map((e: any) => (e && typeof e.text === "string" ? e.text : ""))
              .filter(Boolean)
              .join("\n");
            if (t) return t;
          }
        }
      } catch {
        /* fall through to the DOM scrape */
      }
      try {
        const c = sec && sec._container && (sec._container[0] || sec._container);
        if (!c || !c.querySelectorAll) return "";
        return Array.from(c.querySelectorAll("textarea,input,[contenteditable]"))
          .map((f: any) => f.value || f.textContent || "")
          .filter(Boolean)
          .join("\n");
      } catch {
        return "";
      }
    };

    // Whether a section takes a free-text bullet. STRUCTURED lists/codes don't
    // (bug 2). Self-contained — mirrors STRUCTURED_SECTION_KEYS /
    // isSectionKeyWritable above (executeScript can't reference them). Blocklist
    // is authoritative; a best-effort `isMultilist()` can only SUBTRACT.
    const structuredKeys = [
      "order", "orders", "referenced", "problems", "problemlist",
      "med", "meds", "medication", "medications", "medlist", "medicationlist",
      "allergy", "allergies", "allergylist", "vital", "vitals", "data",
      "cpt", "cptcode", "cptcodes", "dx", "dxcode", "dxcodes",
      "diagnosis", "diagnoses", "prescription", "prescriptions",
      "referral", "referrals", "lab", "labs", "imaging",
      "immunization", "immunizations", "vaccine", "vaccines",
    ];
    // Live section TYPES that can never hold a free-text bullet. Mirrors
    // STRUCTURED_SECTION_TYPES. `textlist` (`referenced`/`orders`) holds
    // DOCUMENT REFERENCES, so prose has no meaning there.
    const structuredTypes = ["textlist"];
    // Mirrors MULTILIST_WRITABLE_KEYS. A `multilist` write was proven live
    // (2026-08-05) for `instr` only. The widget type cannot say whether a list
    // SHOULD hold prose — a structured problem/goals list is a multilist too —
    // so this is allowlisted per key, not granted per type.
    const multilistWritableKeys = ["instr"];
    // Mirrors POLICY_EXCLUDED_SECTION_PATTERN — technically writable, but the
    // wrong home for prose. Separate from structuredKeys, which is about
    // corruption. Inlined: this fn is serialized and closes over nothing.
    const policyExcluded = /procedure/;
    const isWritable = (key: string, sec: any): boolean => {
      const norm = String(key || "").toLowerCase().replace(/[\s_-]+/g, "");
      if (structuredKeys.indexOf(norm) !== -1) return false;
      if (policyExcluded.test(norm)) return false;
      try {
        if (sec) {
          const t =
            typeof sec.getType === "function" ? String(sec.getType()).toLowerCase() : "";
          if (t && structuredTypes.indexOf(t) !== -1) return false;
          if (t === "multilist" && multilistWritableKeys.indexOf(norm) === -1) return false;
        }
      } catch {
        /* API absent / threw — inconclusive, fall through to writable */
      }
      return true;
    };

    // Id of the note open right now. The content-script scrape cannot read
    // this (its hidden-input selectors do not exist on the live page, and
    // `el8` is invisible from the isolated world), so it rides along here —
    // without it the host's write guard refuses every write. Both accessors
    // returned the same id live on 2026-08-05; try the note first, then the
    // notes manager, which also answers for a non-visit note.
    const readOpenNoteId = (): string | undefined => {
      try {
        const direct = chart.visitNote && chart.visitNote.main && chart.visitNote.main.getNoteId
          ? chart.visitNote.main.getNoteId()
          : null;
        if (direct != null && String(direct).length > 0) return String(direct);
      } catch {
        /* fall through to the manager */
      }
      try {
        const mgr = chart.mgr && chart.mgr.notes;
        const id = mgr && mgr.getOpenNoteId ? mgr.getOpenNoteId() : null;
        if (id != null && String(id).length > 0) return String(id);
      } catch {
        /* unavailable — leave undefined, the host guard still fails closed */
      }
      return undefined;
    };

    // The open note's template display name. Same object the stage write
    // already resolves sections through (`getCurrFormTemplate()`), so this adds
    // no new page surface — only its `displayName`, which is the note type.
    // Inlined (this fn is serialized and closes over nothing).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const readTemplateName = (main: any): string | undefined => {
      try {
        const t = typeof main.getCurrFormTemplate === "function" ? main.getCurrFormTemplate() : null;
        const name = t && typeof t.displayName === "string" ? t.displayName.trim() : "";
        return name || undefined;
      } catch {
        return undefined;
      }
    };

    const n = chart.visitNote && chart.visitNote.main;
    if (n && n.isOpen && n.isOpen()) {
      const secs = n._inputSections || {};
      return {
        ok: true,
        engine: "legacy",
        noteId: readOpenNoteId(),
        templateName: readTemplateName(n),
        sections: Object.keys(secs).map((key) => ({
          key,
          label: (() => {
            try {
              return (secs[key] && secs[key].getName && secs[key].getName()) || key;
            } catch {
              return key;
            }
          })(),
          currentText: readSectionText(secs[key], key),
          writable: isWritable(key, secs[key]),
        })),
      };
    }

    const vn2 = chart.visitNote2 && chart.visitNote2.main;
    if (vn2 && vn2.isOpen && vn2.isOpen()) return { ok: true, engine: "vn2", sections: [] };

    const nv = chart.nonvisitNote && chart.nonvisitNote.main;
    if (nv && nv.isOpen && nv.isOpen()) return { ok: true, engine: "nonvisit", sections: [] };

    // The read RAN: the chart is there and nothing editable is open. A signed
    // note lands here, and it is a clinical fact, not a failure.
    return { ok: true, engine: "none", sections: [] };
  } catch {
    return { ok: false, engine: "none", sections: [], failure: "unreadable" };
  }
}
